import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Truck, MapPin, Package, CheckCircle, Clock, Camera, Navigation,
  AlertCircle, XCircle, FileText, User, X, Phone, Play, Flag,
  Star, Shield, Send, Edit3, ChevronRight, ChevronDown, LogOut, Bell, MessageCircle, TrendingUp, Award, Zap, Share2, Menu, ImagePlus, WifiOff, RefreshCw
} from 'lucide-react';
import { useStore, getStore, actualizarStatusOrden, completarOrdenChofer, reportarIncidencia, reanudarOrdenChofer, getNotificacionesPendientes, marcarNotificacionesLeidas, limpiarNotificaciones, actualizarFotoUsuario, actualizarPerfilChofer } from '../store';
import ChatModal from '../components/ChatModal';
import FeedbackButton from '../components/FeedbackButton';
import TipoUnidadBadge from '../components/TruckTypeIcon';
import { solicitarPermisoNotif, fireNotifBrowser, playNotifSound, playOrdenSound, vibrarOrden, fireNotifOrden, vibrarInicioViaje, vibrarPasoViaje, vibrarEntregaFinal } from '../notifHelper';
import { useApiStore } from '../hooks/useApiStore';
import { apiActualizarStatusOrden, apiCompletarOrden, apiMarcarLeidas, apiEliminarTodasNotificaciones, apiActualizarPerfil, guardarUsuario, apiActualizarPosicionChofer, apiActualizarPosicionLibre, apiReportarIncidencia, apiCambiarPassword } from '../api';
import { useOfflineQueue } from '../hooks/useOfflineQueue';

// ── Constantes fuera del componente: se crean UNA vez, no en cada render ──
const STATUS_COLORS = {
  asignada: 'bg-blue-900/40 text-blue-300 border-blue-700',
  aceptada: 'bg-purple-900/40 text-purple-300 border-purple-700',
  en_camino_recogida: 'bg-indigo-950/40 text-indigo-300 border-indigo-700',
  cargando: 'bg-yellow-950/40 text-yellow-300 border-yellow-700',
  en_ruta: 'bg-orange-900/30 text-orange-300 border-orange-700',
  llegada_destino: 'bg-teal-900/40 text-teal-300 border-teal-700',
  descargando: 'bg-cyan-900/40 text-cyan-300 border-cyan-700',
  completada: 'bg-green-900/40 text-green-300 border-green-700',
  incidencia: 'bg-red-900/40 text-red-300 border-red-700',
};
const STATUS_TEXTS = {
  asignada: 'Asignada', aceptada: 'Aceptada',
  en_camino_recogida: 'En Camino a Recoger', cargando: 'Cargando Mercancía',
  en_ruta: 'En Ruta', llegada_destino: 'En Destino',
  descargando: 'Descargando', completada: 'Completada', incidencia: 'Con Incidencia',
};
const PRIORIDAD_COLORS = {
  urgente: 'bg-red-500 text-white', alta: 'bg-orange-500 text-white', normal: 'bg-gray-500 text-white',
};
const TIPOS_EVIDENCIA = [
  { tipo: 'foto_entrega', label: 'Foto de Entrega', sub: 'Mercancía entregada al cliente', color: 'blue' },
  { tipo: 'foto_mercancia', label: 'Estado de la carga', sub: 'Condición al momento de entregar', color: 'green' },
  { tipo: 'documento', label: 'Documento / Remisión', sub: 'Foto del documento firmado', color: 'purple' },
];
const STATUS_PENDIENTES = ['asignada', 'aceptada'];
const STATUS_EN_PROGRESO = ['en_camino_recogida', 'cargando', 'en_ruta', 'llegada_destino', 'descargando', 'incidencia'];

function AppChofer({ usuario, onLogout }) {
  const { store, dispatch, recargar } = useApiStore('chofer');

  // ========== ESTADOS DE LA APLICACIÓN ==========
  const [seccion, setSeccion] = useState('ordenes');
  const [ordenActiva, setOrdenActiva] = useState(null);
  const [chatModal, setChatModal] = useState(null); // { viajeId, contraparte }
  const [mostrarIncidencia, setMostrarIncidencia] = useState(false);
  const [mostrarEvidencia, setMostrarEvidencia] = useState(false);
  const [mostrarFirma, setMostrarFirma] = useState(false);
  const [mostrarDetalleOrden, setMostrarDetalleOrden] = useState(null);
  const [posicionActual, setPosicionActual] = useState(null); // { lat, lng, precision }
  const [gpsActivo, setGpsActivo] = useState(false);
  // Un watch de geolocalización por orden — un chofer puede tener más de una orden en
  // progreso a la vez (multi-drop). Antes se guardaba un solo watchId en un useState y cada
  // iniciarGPS() lo pisaba, así que detenerGPS() de UNA orden terminaba cancelando el
  // tracking de la OTRA (revisión frontend 2026-08-22).
  const gpsWatchIdsRef = useRef({});
  const [sidebarAbierto, setSidebarAbierto] = useState(false);
  const [notificacion, setNotificacion] = useState(null);
  const [mostrarNotificaciones, setMostrarNotificaciones] = useState(false);
  const _chatSeenKey = `choyero_chat_seen_${usuario?.id || 'guest'}`;
  const [lastSeenMessages, setLastSeenMessages] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(_chatSeenKey) || '{}'); } catch { return {}; }
  });
  const [toastNotif, setToastNotif] = useState([]);
  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const avatar = ev.target.result;
      dispatch(s => ({ ...s, usuarioPerfil: { ...(s.usuarioPerfil || usuario || {}), avatar } }));
      try {
        const updated = await apiActualizarPerfil({ avatar });
        guardarUsuario({ ...usuario, ...updated });
        dispatch(s => ({ ...s, usuarioPerfil: { ...(s.usuarioPerfil || {}), ...updated } }));
      } catch { /* ya se actualizó localmente */ }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  const [toastPausado, setToastPausado] = useState(false);
  useEffect(() => { solicitarPermisoNotif(); }, []);

  // Alerta de nueva orden: sonido urgente + vibración + notificación del browser
  useEffect(() => {
    const handle = (e) => {
      const orden = e.detail?.orden;
      playOrdenSound();
      vibrarOrden();
      fireNotifOrden(
        '🚚 ¡Nueva orden de trabajo!',
        orden ? `${orden.origen} → ${orden.destino}` : 'Tienes una nueva orden asignada.'
      );
    };
    window.addEventListener('choyero_nueva_orden', handle);
    return () => window.removeEventListener('choyero_nueva_orden', handle);
  }, []);

  // ========== NOTIFICACIONES DEL STORE ==========
  const viewedNotifIds = useRef(new Set()); // IDs vistos esta sesión — evita que el badge reaparezca tras reload
  const gpsFailCount = useRef(0);

  // Memoizado: evita filtrar 3 veces por render en el panel de notificaciones
  const misNotificaciones = useMemo(() =>
    (store.notificaciones || []).filter(n => n.destinatarioId === usuario?.id),
  [store.notificaciones, usuario?.id]); // eslint-disable-line

  const misNotificacionesOrdenadas = useMemo(() =>
    misNotificaciones.slice().sort((a, b) => new Date(b.creadoEn) - new Date(a.creadoEn)),
  [misNotificaciones]);

  const notifsPendientes = useMemo(() =>
    misNotificaciones.filter(n => !n.leida && !viewedNotifIds.current.has(n.id)),
  [misNotificaciones]); // eslint-disable-line

  // Toasts: solo desde evento centralizado de useApiStore (nunca desde polling directo)
  useEffect(() => {
    const handle = (e) => {
      const nuevas = e.detail?.notificaciones || [];
      if (nuevas.length === 0) return;
      setToastNotif(q => [...q, ...nuevas]);
      // Marcar como leídas al mostrarse en pantalla — evita que reaparezcan en la siguiente sesión
      marcarNotificacionesLeidas(usuario?.id, 'chofer');
      apiMarcarLeidas().catch(() => {});
    };
    window.addEventListener('choyero_notif_nueva', handle);
    return () => window.removeEventListener('choyero_notif_nueva', handle);
  }, [usuario?.id]); // eslint-disable-line

  // Detectar nuevos mensajes de chat vía evento de socket y mostrar toast
  const mensajesYaToastados = useRef(new Set());
  useEffect(() => {
    const handle = (e) => {
      if (e.detail?.evento !== 'nuevo_mensaje') return;
      const m = e.detail?.data?.mensaje;
      if (!m || m.emisorId === usuario?.id) return;
      if (mensajesYaToastados.current.has(m.id)) return;
      mensajesYaToastados.current.add(m.id);
      if (m.destinatarioRol && m.destinatarioRol !== 'chofer') return;
      const chatAbierto = chatModal && chatModal.viajeId === m.viajeId;
      if (chatAbierto) return;
      const preview = (m.texto?.length ?? 0) > 40 ? m.texto.slice(0, 40) + '…' : (m.texto || '');
      const emisor = m.emisorNombre || 'Usuario';
      setToastNotif(q => [...q, { tipo: 'chat_mensaje', titulo: `💬 Mensaje de ${emisor}`, mensaje: preview, datos: { viajeId: m.viajeId }, contraparte: { id: m.emisorId, nombre: emisor, rol: m.emisorRol || 'transportista' } }]);
      playNotifSound();
      fireNotifBrowser(`💬 Mensaje de ${emisor}`, preview);
    };
    window.addEventListener('choyero_realtime', handle);
    return () => window.removeEventListener('choyero_realtime', handle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario?.id, chatModal]);

  const cerrarToast = () => {
    setToastNotif(q => {
      if (q[0]?.id) marcarNotificacionesLeidas(usuario?.id, 'chofer');
      return q.slice(1);
    });
    setToastPausado(true);
  };

  // Mostrar siguiente notificación cuando el modal de orden se cierra
  useEffect(() => {
    if (toastPausado && !mostrarDetalleOrden) {
      setToastPausado(false);
    }
  }, [mostrarDetalleOrden, toastPausado]);

  const abrirNotificaciones = useCallback(() => {
    setMostrarNotificaciones(true);
    misNotificaciones.filter(n => !n.leida).forEach(n => viewedNotifIds.current.add(n.id));
    marcarNotificacionesLeidas(usuario?.id, 'chofer');
    apiMarcarLeidas().catch(e => console.warn('[Chofer] Error marcando leídas:', e.message));
    dispatch(s => ({
      ...s,
      notificaciones: (s.notificaciones || []).map(n =>
        n.destinatarioId === usuario?.id ? { ...n, leida: true } : n
      )
    }));
  }, [misNotificaciones, usuario?.id, dispatch]); // eslint-disable-line
  // Estados de evidencia/firma keyed por ordenId (soporta múltiples órdenes activas)
  const [evidenciasPorOrden, setEvidenciasPorOrden] = useState({});
  const [firmaPorOrden, setFirmaPorOrden] = useState({});
  const [firmaDatoPorOrden, setFirmaDatoPorOrden] = useState({});
  const [gpsPorOrden, setGpsPorOrden] = useState({});
  const [mostrarResumen, setMostrarResumen] = useState(false);
  const fotoInputRef = useRef(null);
  const fotoGaleriaRef = useRef(null);
  // Refs del canvas de firma — deben vivir en el padre para no violar Rules of Hooks
  const firmaCanvasRef = useRef(null);
  const firmaDibujandoRef = useRef(false);
  const firmaLastPos = useRef(null);
  const [firmaTieneFirma, setFirmaTieneFirma] = useState(false);
  const [fotoTipo, setFotoTipo] = useState(null);

  // Canvas de firma siempre en landscape (1600×1200) vía CSS transform rotate
  const { isOnline, enqueue, syncing, queueSize, lastDropped } = useOfflineQueue();
  useEffect(() => {
    if (lastDropped) mostrarNotificacion('No se pudo sincronizar un paso guardado sin señal. Verifica el estado del viaje.', 'error');
  }, [lastDropped]); // eslint-disable-line

  // Tiempo transcurrido en la orden activa
  const [tiempoTranscurrido, setTiempoTranscurrido] = useState('');
  // Edición de perfil
  const [editandoPerfil, setEditandoPerfil] = useState(false);
  const [formPerfil, setFormPerfil] = useState({});


  // ========== DATOS DEL CHOFER (desde usuario + store) ==========
  const choferInfo = useMemo(() => {
    const choferVivo = (store.usuarios || []).find(u => u.id === usuario?.id);
    const perfil = store.usuarioPerfil || usuario;
    // Entregas reales del chofer desde historial (viajesCompletados se incrementa en transportistaId, no en chofer)
    const entregasReales = (store.historialEntregas || []).length;
    // Antigüedad calculada desde creadoEn
    const creadoEn = perfil?.creadoEn || usuario?.creadoEn;
    let antiguedad = '—';
    if (creadoEn) {
      const meses = Math.floor((Date.now() - new Date(creadoEn).getTime()) / (1000 * 60 * 60 * 24 * 30));
      antiguedad = meses < 1 ? 'Menos de 1 mes' : meses < 12 ? `${meses} mes${meses > 1 ? 'es' : ''}` : `${Math.floor(meses / 12)} año${Math.floor(meses / 12) > 1 ? 's' : ''}`;
    }
    return {
      nombre: perfil?.nombre || usuario?.nombre || '',
      licencia: perfil?.licencia || usuario?.licencia || '—',
      telefono: perfil?.telefono || usuario?.telefono || '—',
      empresa: perfil?.empresaNombre || usuario?.empresaNombre || '—',
      unidadAsignada: perfil?.unidadAsignada || usuario?.unidadAsignada || null,
      placas: perfil?.placas || usuario?.placas || null,
      tipoUnidadAsignada: perfil?.tipoUnidadAsignada || usuario?.tipoUnidadAsignada || null,
      fotoUnidadAsignada: perfil?.fotoUnidadAsignada || usuario?.fotoUnidadAsignada || null,
      marcaUnidadAsignada: perfil?.marcaUnidadAsignada || usuario?.marcaUnidadAsignada || null,
      modeloUnidadAsignada: perfil?.modeloUnidadAsignada || usuario?.modeloUnidadAsignada || null,
      anioUnidadAsignada: perfil?.anioUnidadAsignada || usuario?.anioUnidadAsignada || null,
      capacidadUnidadAsignada: perfil?.capacidadUnidadAsignada || usuario?.capacidadUnidadAsignada || null,
      gpsUnidadAsignada: perfil?.gpsUnidadAsignada ?? usuario?.gpsUnidadAsignada ?? false,
      largoMUnidadAsignada: perfil?.largoMUnidadAsignada || usuario?.largoMUnidadAsignada || null,
      anchoMUnidadAsignada: perfil?.anchoMUnidadAsignada || usuario?.anchoMUnidadAsignada || null,
      altoMUnidadAsignada: perfil?.altoMUnidadAsignada || usuario?.altoMUnidadAsignada || null,
      seguroCargaUnidadAsignada: perfil?.seguroCargaUnidadAsignada ?? usuario?.seguroCargaUnidadAsignada ?? false,
      tempMinimaUnidadAsignada: perfil?.tempMinimaUnidadAsignada || usuario?.tempMinimaUnidadAsignada || null,
      viajesCompletados: entregasReales,
      calificacion: choferVivo?.calificacion ?? perfil?.calificacion ?? usuario?.calificacion ?? 0,
      kmRecorridos: perfil?.kmRecorridos || usuario?.kmRecorridos || 0,
      antiguedad,
    };
  }, [store.usuarios, store.usuarioPerfil, store.historialEntregas, usuario]); // eslint-disable-line

  // ========== ÓRDENES DESDE EL STORE COMPARTIDO ==========

  const misOrdenes = useMemo(() => store.ordenesChofer.filter(
    o => (o.choferId === usuario?.id || o.choferEmail === usuario?.email) &&
         o.status !== 'completada' && o.status !== 'cancelada'
  ), [store.ordenesChofer, usuario?.id, usuario?.email]); // eslint-disable-line

  const ordenesAsignadas = useMemo(() => misOrdenes.filter(o => STATUS_PENDIENTES.includes(o.status)), [misOrdenes]); // eslint-disable-line
  const ordenesEnProgreso = useMemo(() => misOrdenes.filter(o => STATUS_EN_PROGRESO.includes(o.status)), [misOrdenes]); // eslint-disable-line

  // ========== MULTI-ORDEN EN CURSO ==========
  const [ordenActivaId, setOrdenActivaId] = useState(null);
  const [ordenesEnCursoLocal, setOrdenesEnCursoLocal] = useState({});
  const [expandedOrden, setExpandedOrden] = useState(null);

  // Orden en curso actualmente seleccionada
  const ordenEnCurso = useMemo(() => {
    const id = ordenActivaId || ordenesEnProgreso[0]?.id;
    if (!id) return null;
    return ordenesEnCursoLocal[id] || ordenesEnProgreso.find(o => o.id === id) || null;
  }, [ordenActivaId, ordenesEnProgreso, ordenesEnCursoLocal]); // eslint-disable-line

  // Helpers para derivar estado por orden activa
  const _oid = ordenEnCurso?.id;
  const evidenciasSubidas = evidenciasPorOrden[_oid] || [];
  const firmaCliente = !!firmaPorOrden[_oid];
  const firmaData = firmaDatoPorOrden[_oid] || null;
  const entregaGPS = gpsPorOrden[_oid] || null;
  const setEvidenciasSubidas = (updater) => setEvidenciasPorOrden(prev => {
    const curr = prev[_oid] || [];
    return { ...prev, [_oid]: typeof updater === 'function' ? updater(curr) : updater };
  });
  const setFirmaCliente = (val) => setFirmaPorOrden(prev => ({ ...prev, [_oid]: val }));
  const setFirmaData = (val) => setFirmaDatoPorOrden(prev => ({ ...prev, [_oid]: val }));
  const setEntregaGPS = (val) => setGpsPorOrden(prev => ({ ...prev, [_oid]: val }));

  // Helper local para actualizar orden en curso (merge)
  const setOrdenEnCurso = (updaterOrValue) => {
    setOrdenesEnCursoLocal(prev => {
      if (updaterOrValue === null) return prev; // use setOrdenActivaId(null) to deselect
      const id = ordenActivaId || ordenesEnProgreso[0]?.id;
      if (!id) return prev;
      const curr = prev[id] || ordenesEnProgreso.find(o => o.id === id) || {};
      const next = typeof updaterOrValue === 'function' ? updaterOrValue(curr) : updaterOrValue;
      return { ...prev, [next.id || id]: next };
    });
  };

  // Sincronizar con el store (restaura progreso/checkpoints)
  useEffect(() => {
    setOrdenesEnCursoLocal(prev => {
      const next = { ...prev };
      let changed = false;
      ordenesEnProgreso.forEach(o => {
        const local = prev[o.id];
        if (!local) {
          next[o.id] = o; changed = true;
        } else {
          const newCheckpoints = (o.checkpoints?.length > (local.checkpoints?.length || 0))
            ? o.checkpoints : (local.checkpoints || []);
          const statusChanged = o.status !== local.status;
          const progresoChanged = o.progreso !== undefined && o.progreso !== local.progreso;
          const checkpointsChanged = newCheckpoints !== local.checkpoints;
          if (statusChanged || progresoChanged || checkpointsChanged) {
            next[o.id] = {
              ...local,
              status: o.status,
              statusAnterior: o.statusAnterior ?? local.statusAnterior,
              progreso: o.progreso ?? local.progreso,
              checkpoints: newCheckpoints,
            };
            changed = true;
          }
        }
      });
      Object.keys(prev).forEach(id => {
        if (!ordenesEnProgreso.find(o => o.id === id)) { delete next[id]; changed = true; }
      });
      return changed ? next : prev;
    });
    setOrdenActivaId(prev => {
      if (prev && ordenesEnProgreso.find(o => o.id === prev)) return prev;
      return ordenesEnProgreso.length > 0 ? ordenesEnProgreso[0].id : null;
    });
  }, [store.ordenesChofer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tiempo transcurrido en la orden activa
  useEffect(() => {
    if (!ordenEnCurso?.fechaInicioTs) { setTiempoTranscurrido(''); return; }
    const update = () => {
      const diff = Date.now() - ordenEnCurso.fechaInicioTs;
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTiempoTranscurrido(h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    update();
    const iv = setInterval(update, 60000);
    return () => clearInterval(iv);
  }, [ordenEnCurso?.fechaInicioTs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cambiar a otra orden activa (con limpieza de evidencias de la orden previa)
  const cambiarOrdenActiva = (orden) => {
    setOrdenActivaId(orden.id);
    mostrarNotificacion(`Cambiaste a orden ${orden.id}`);
  };

  // ========== HISTORIAL DESDE STORE ==========
  const historialStore = useMemo(() => store.historialEntregas.filter(h =>
    h.choferId === usuario?.id || h.choferEmail === usuario?.email
  ), [store.historialEntregas, usuario?.id, usuario?.email]); // eslint-disable-line
  const historial = useMemo(() => historialStore.slice().reverse(), [historialStore]);

  // Estadísticas pre-computadas — evita recalcular en cada render por polling (cada 8s)
  const rendimientoStats = useMemo(() => {
    const mesActual = new Date().toISOString().slice(0, 7);
    const entregasMes = historialStore.filter(h => (h.creadoEn || h.fecha || '').startsWith(mesActual));
    const calificadas = historial.filter(h => h.calificacionRecibida > 0);
    const promCal = calificadas.length > 0
      ? (calificadas.reduce((a, h) => a + h.calificacionRecibida, 0) / calificadas.length).toFixed(1)
      : null;
    const totalCompletadas = historial.filter(h => ['completada', 'entregada'].includes(h.status)).length;
    const conIncidencia = (store.ordenesChofer || []).filter(o => o.status === 'incidencia').length;
    const sinIncidencia = totalCompletadas > 0
      ? Math.round(((totalCompletadas - conIncidencia) / totalCompletadas) * 100) : null;
    let racha = 0;
    for (let i = 0; i < historial.length; i++) {
      if (['completada', 'entregada'].includes(historial[i].status)) racha++;
      else break;
    }
    const porMes = {};
    historialStore.forEach(h => {
      const mes = (h.creadoEn || h.fecha || '').slice(0, 7);
      if (mes) porMes[mes] = (porMes[mes] || 0) + 1;
    });
    const mejorMes = Object.entries(porMes).sort((a, b) => b[1] - a[1])[0];
    const dist = [5, 4, 3, 2, 1].map(n => ({
      val: n,
      count: calificadas.filter(h => h.calificacionRecibida === n).length,
    }));
    return { entregasMes, calificadas, promCal, sinIncidencia, racha, porMes, mejorMes, dist };
  }, [historialStore, historial, store.ordenesChofer]); // eslint-disable-line

  const historialStats = useMemo(() => {
    const mesActual = new Date().toISOString().slice(0, 7);
    const entregasMes = historialStore.filter(h => (h.creadoEn || h.fecha || '').startsWith(mesActual));
    const totalComp = historial.filter(h => ['completada', 'entregada'].includes(h.status)).length;
    const conInc = (store.ordenesChofer || []).filter(o => o.status === 'incidencia').length;
    const sinInc = totalComp > 0 ? Math.round(((totalComp - conInc) / totalComp) * 100) : null;
    return { entregasMes, sinInc };
  }, [historialStore, historial, store.ordenesChofer]); // eslint-disable-line

  // ========== FUNCIONES AUXILIARES ==========
  const getStatusColor = (status) => STATUS_COLORS[status] || 'bg-gray-900 text-gray-300 border-gray-700';
  const getStatusText = (status) => STATUS_TEXTS[status] || status;
  const getPrioridadColor = (prioridad) => PRIORIDAD_COLORS[prioridad] || 'bg-gray-500 text-white';

  const mostrarNotificacion = (mensaje, tipo = 'success', persistente = false, opciones = {}) => {
    const titulos = { success: '✓ Listo', warning: '⚠ Atención', error: '✗ Error' };
    setToastNotif(q => [...q, { tipo: `alerta_${tipo}`, titulo: titulos[tipo] || 'Aviso', mensaje, persistente, ...opciones }]);
  };


  // Auto-cerrar toasts: alertas simples en 3.5s, notificaciones del sistema en 8s
  useEffect(() => {
    if (toastNotif.length === 0 || toastPausado) return;
    const notif = toastNotif[0];
    if (notif?.persistente) return;
    const delay = notif?.tipo?.startsWith('alerta_') ? 3500 : 8000;
    const timer = setTimeout(() => setToastNotif(q => q.slice(1)), delay);
    return () => clearTimeout(timer);
  }, [toastNotif.length, toastPausado]); // eslint-disable-line

  // Recordatorios automáticos con delay — cleanup correcto para evitar memory leak
  useEffect(() => {
    const _ssKey = `choyero_rec_chofer_${usuario?.id}`;
    const checkRecordatorios = () => {
      const s = getStore();
      const ahora = Date.now();
      const last = JSON.parse(sessionStorage.getItem(_ssKey) || '{}');
      const ordenesPendientes = (s.ordenesChofer || []).filter(o =>
        o.choferId === usuario?.id && o.status === 'asignada'
      );
      if (ordenesPendientes.length > 0 && (!last.ordenes || ahora - last.ordenes > 5 * 60 * 1000)) {
        last.ordenes = ahora;
        sessionStorage.setItem(_ssKey, JSON.stringify(last));
        mostrarNotificacion(
          `🚚 Tienes ${ordenesPendientes.length} orden(es) asignada(s) pendiente(s) de iniciar. ¡Inicia cuando estés listo!`,
          'warning', true, { navegar: 'ordenes' }
        );
      }
    };
    let interval = null;
    const timer = setTimeout(() => {
      checkRecordatorios();
      interval = setInterval(checkRecordatorios, 5 * 60 * 1000);
    }, 3 * 60 * 1000);
    return () => { clearTimeout(timer); if (interval) clearInterval(interval); };
  }, [usuario?.id]); // eslint-disable-line

  // ========== FUNCIONES DE ACCIONES ==========

  // Iniciar orden (ir a recoger) → actualiza store y estado local
  const iniciarOrden = async (orden) => {
    vibrarInicioViaje();
    const hora = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    const ordenIniciada = {
      ...orden,
      status: 'en_camino_recogida',
      horaInicio: hora,
      fechaInicioTs: Date.now(),
      progreso: 0,
      checkpoints: [{ paso: 'Orden iniciada', hora, completado: true }]
    };
    // Actualizar estado local siempre (optimista)
    actualizarStatusOrden(orden.id, 'en_camino_recogida');
    setOrdenActivaId(orden.id);
    setOrdenesEnCursoLocal(prev => ({ ...prev, [orden.id]: ordenIniciada }));
    setSeccion('en_curso');
    iniciarGPS(orden.id);
    if (isOnline) {
      apiActualizarStatusOrden(orden.id, 'en_camino_recogida').catch(() => {
        enqueue({ action: 'updateStatus', ordenId: orden.id, status: 'en_camino_recogida' });
      });
      mostrarNotificacion('¡Orden iniciada! GPS activado. Dirígete al punto de recogida.');
    } else {
      enqueue({ action: 'updateStatus', ordenId: orden.id, status: 'en_camino_recogida' });
      mostrarNotificacion('¡Orden iniciada sin señal! Se sincronizará al recuperar conexión.', 'warning');
    }
  };

  // Confirmar llegada al origen → actualiza store
  const confirmarLlegadaOrigen = async () => {
    vibrarPasoViaje();
    const hora = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    actualizarStatusOrden(ordenEnCurso.id, 'cargando');
    setOrdenEnCurso({
      ...ordenEnCurso, status: 'cargando', progreso: 25,
      checkpoints: [...(ordenEnCurso.checkpoints || []), { paso: 'Llegada al origen', hora, completado: true }]
    });
    if (isOnline) {
      apiActualizarStatusOrden(ordenEnCurso.id, 'cargando').catch(() => {
        enqueue({ action: 'updateStatus', ordenId: ordenEnCurso.id, status: 'cargando' });
      });
      mostrarNotificacion('Llegada confirmada. Procede a cargar la mercancía.');
    } else {
      enqueue({ action: 'updateStatus', ordenId: ordenEnCurso.id, status: 'cargando' });
      mostrarNotificacion('Sin señal — paso guardado. Se sincronizará al recuperar conexión.', 'warning');
    }
  };

  // Confirmar carga completada → actualiza store
  const confirmarCargaIniciada = async () => {
    vibrarPasoViaje();
    const hora = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    actualizarStatusOrden(ordenEnCurso.id, 'en_ruta');
    setOrdenEnCurso({
      ...ordenEnCurso, status: 'en_ruta', progreso: 50,
      checkpoints: [...(ordenEnCurso.checkpoints || []), { paso: 'Carga completada - En ruta', hora, completado: true }]
    });
    if (isOnline) {
      apiActualizarStatusOrden(ordenEnCurso.id, 'en_ruta').catch(() => {
        enqueue({ action: 'updateStatus', ordenId: ordenEnCurso.id, status: 'en_ruta' });
      });
      mostrarNotificacion('¡En ruta hacia el destino! Conduce con precaución.');
    } else {
      enqueue({ action: 'updateStatus', ordenId: ordenEnCurso.id, status: 'en_ruta' });
      mostrarNotificacion('Sin señal — paso guardado. Se sincronizará al recuperar conexión.', 'warning');
    }
  };

  // Confirmar llegada al destino → actualiza store + captura GPS
  const confirmarLlegadaDestino = async () => {
    vibrarPasoViaje();
    const hora = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    actualizarStatusOrden(ordenEnCurso.id, 'llegada_destino');
    setOrdenEnCurso({
      ...ordenEnCurso, status: 'llegada_destino', progreso: 75,
      checkpoints: [...(ordenEnCurso.checkpoints || []), { paso: 'Llegada al destino', hora, completado: true }]
    });
    detenerGPS(ordenEnCurso.id);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setEntregaGPS({ lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6), precision: Math.round(pos.coords.accuracy) }),
        () => {}
      );
    }
    if (isOnline) {
      apiActualizarStatusOrden(ordenEnCurso.id, 'llegada_destino').catch(() => {
        enqueue({ action: 'updateStatus', ordenId: ordenEnCurso.id, status: 'llegada_destino' });
      });
      mostrarNotificacion('¡Llegaste al destino! Procede a descargar y obtener evidencias.');
    } else {
      enqueue({ action: 'updateStatus', ordenId: ordenEnCurso.id, status: 'llegada_destino' });
      mostrarNotificacion('Sin señal — llegada guardada. Se sincronizará al recuperar conexión.', 'warning');
    }
  };

  // Confirmar inicio de descarga → transición llegada_destino → descargando
  const confirmarInicioDescarga = async () => {
    vibrarPasoViaje();
    const hora = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    actualizarStatusOrden(ordenEnCurso.id, 'descargando');
    setOrdenEnCurso({
      ...ordenEnCurso, status: 'descargando', progreso: 85,
      checkpoints: [...(ordenEnCurso.checkpoints || []), { paso: 'Inicio de descarga', hora, completado: true }]
    });
    if (isOnline) {
      apiActualizarStatusOrden(ordenEnCurso.id, 'descargando').catch(() => {
        enqueue({ action: 'updateStatus', ordenId: ordenEnCurso.id, status: 'descargando' });
      });
      mostrarNotificacion('Descarga iniciada. Registra las evidencias y obtén la firma.');
    } else {
      enqueue({ action: 'updateStatus', ordenId: ordenEnCurso.id, status: 'descargando' });
      mostrarNotificacion('Sin señal — paso guardado. Se sincronizará al recuperar conexión.', 'warning');
    }
  };

  // Abrir cámara para tomar foto real
  const subirEvidencia = (tipo) => {
    setFotoTipo(tipo);
    if (fotoInputRef.current) {
      fotoInputRef.current.value = '';
      fotoInputRef.current.click();
    }
  };

  // Abrir galería de fotos
  const subirDesdeGaleria = (tipo) => {
    setFotoTipo(tipo);
    if (fotoGaleriaRef.current) {
      fotoGaleriaRef.current.value = '';
      fotoGaleriaRef.current.click();
    }
  };

  // Procesar foto capturada → base64
  const onFotoCapturada = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const nombreMap = { foto_entrega: 'Foto de entrega', foto_mercancia: 'Foto de mercancía', documento: 'Documento firmado' };
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1024;
        const ratio = Math.min(MAX / img.width, MAX / img.height, 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const base64 = canvas.toDataURL('image/jpeg', 0.75);
        const nueva = {
          id: Date.now(),
          tipo: fotoTipo,
          base64,
          hora: new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }),
          nombre: nombreMap[fotoTipo] || 'Foto'
        };
        setEvidenciasSubidas(prev => {
          const sin = prev.filter(ev => ev.tipo !== fotoTipo);
          return [...sin, nueva];
        });
        mostrarNotificacion(`${nueva.nombre} capturada.`);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Confirmar firma del cliente (recibe dataUrl del canvas)
  const confirmarFirma = (dataUrl) => {
    setFirmaData(dataUrl);
    setFirmaCliente(true);
    setMostrarFirma(false);
    mostrarNotificacion('Firma del cliente registrada.');
  };

  // Completar orden → muestra resumen primero
  const completarOrden = () => {
    if (evidenciasSubidas.length < 2) {
      mostrarNotificacion('Debes tomar al menos 2 fotos de evidencia', 'warning');
      return;
    }
    if (!firmaCliente) {
      mostrarNotificacion('Debes obtener la firma del cliente', 'warning');
      return;
    }
    vibrarPasoViaje();
    setMostrarResumen(true);
  };

  // Confirmar desde el resumen → escribe a API y store
  const confirmarEntregaFinal = async () => {
    vibrarEntregaFinal();
    const idCompletada = ordenEnCurso.id;
    const siguienteOrden = ordenesEnProgreso.find(o => o.id !== idCompletada);

    // Limpiar estado local de esta orden antes de navegar
    setMostrarResumen(false);
    setOrdenesEnCursoLocal(prev => { const n = { ...prev }; delete n[idCompletada]; return n; });
    setEvidenciasPorOrden(prev => { const n = { ...prev }; delete n[idCompletada]; return n; });
    setFirmaPorOrden(prev => { const n = { ...prev }; delete n[idCompletada]; return n; });
    setFirmaDatoPorOrden(prev => { const n = { ...prev }; delete n[idCompletada]; return n; });
    setGpsPorOrden(prev => { const n = { ...prev }; delete n[idCompletada]; return n; });

    // Navegar inmediatamente para que no se vea la orden "colgada"
    if (siguienteOrden) {
      setOrdenActivaId(siguienteOrden.id);
      setSeccion('en_curso');
    } else {
      setOrdenActivaId(null);
      setSeccion('historial');
    }

    // Marcar completada en el store local (optimista, igual que los demás pasos del flujo)
    dispatch(s => ({
      ...s,
      ordenesChofer: (s.ordenesChofer || []).map(o =>
        o.id === idCompletada ? { ...o, status: 'completada', progreso: 100 } : o
      ),
      historialEntregas: [...(s.historialEntregas || []), { id: idCompletada }],
    }));
    completarOrdenChofer(idCompletada, evidenciasSubidas, firmaData);

    // Llamar a la API — si falla o no hay señal, se encola para reintentar (igual que los
    // demás pasos del flujo) en vez de dar por hecho que se guardó en el servidor.
    let sincronizado = false;
    if (isOnline) {
      try {
        await apiCompletarOrden(idCompletada, { evidencias: evidenciasSubidas, firma: firmaData });
        sincronizado = true;
      } catch (e) {
        enqueue({ action: 'completarOrden', ordenId: idCompletada, evidencias: evidenciasSubidas, firma: firmaData });
      }
    } else {
      enqueue({ action: 'completarOrden', ordenId: idCompletada, evidencias: evidenciasSubidas, firma: firmaData });
    }

    if (sincronizado) {
      await recargar();
      mostrarNotificacion(
        siguienteOrden ? '¡Orden completada! Ahora en: ' + siguienteOrden.id : '¡Orden completada exitosamente! Buen trabajo.',
        'success'
      );
    } else {
      mostrarNotificacion('Entrega guardada sin señal — se enviará al servidor en cuanto haya conexión.', 'warning');
    }
  };

  // Abrir navegación GPS
  // punto: 'origen' → navega al punto de recogida
  //        'destino' → navega al punto de entrega
  //        'ruta' → muestra ruta completa origen→destino
  const abrirNavegacion = (orden, punto = 'ruta') => {
    // Parsea coords que pueden llegar como objeto o como string JSON de Prisma
    const parseCoords = (c) => {
      if (!c) return null;
      if (typeof c === 'string') { try { return JSON.parse(c); } catch { return null; } }
      return c;
    };

    // Busca coords en la orden, luego en el viaje del store, luego en la solicitud
    const resolverCoords = (tipo) => {
      const field = tipo === 'origen' ? 'origenCoords' : 'destinoCoords';
      const c1 = parseCoords(orden[field]);
      if (c1?.lat && c1?.lng) return c1;
      const viaje = (store.viajes || []).find(v => v.id === orden.viajeId);
      const c2 = parseCoords(viaje?.[field]);
      if (c2?.lat && c2?.lng) return c2;
      const solicitud = (store.solicitudes || []).find(s => s.id === viaje?.solicitudId);
      const c3 = parseCoords(solicitud?.[field]);
      if (c3?.lat && c3?.lng) return c3;
      return null;
    };

    const formatCoords = (coords, textoFallback) =>
      coords ? `${coords.lat},${coords.lng}` : encodeURIComponent(textoFallback);

    let url;
    if (punto === 'origen') {
      const dest = formatCoords(resolverCoords('origen'), orden.origenDir || orden.origen);
      url = `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
    } else if (punto === 'destino') {
      const dest = formatCoords(resolverCoords('destino'), orden.destinoDir || orden.destino);
      url = `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
    } else {
      const org = formatCoords(resolverCoords('origen'), orden.origenDir || orden.origen);
      const dst = formatCoords(resolverCoords('destino'), orden.destinoDir || orden.destino);
      url = `https://www.google.com/maps/dir/?api=1&origin=${org}&destination=${dst}&travelmode=driving`;
    }
    window.open(url, '_blank');
    mostrarNotificacion('Abriendo navegación GPS...');
  };

  // Calcular distancia en línea recta entre dos coordenadas (Haversine)
  const calcularDistanciaKm = (c1, c2) => {
    if (!c1 || !c2) return null;
    const R = 6371;
    const dLat = (c2.lat - c1.lat) * Math.PI / 180;
    const dLng = (c2.lng - c1.lng) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(c1.lat * Math.PI / 180) * Math.cos(c2.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    const km = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
  };

  // Llamar al cliente
  const llamarCliente = (telefono) => {
    window.open(`tel:${telefono}`);
  };

  // GPS continuo — iniciar tracking
  const iniciarGPS = (ordenId) => {
    if (!navigator.geolocation) {
      mostrarNotificacion('GPS no disponible en este dispositivo/navegador', 'error');
      return;
    }
    // Si ya había un watch para esta misma orden (doble llamada), limpiarlo antes de crear otro
    if (gpsWatchIdsRef.current[ordenId] != null) {
      navigator.geolocation.clearWatch(gpsWatchIdsRef.current[ordenId]);
    }
    let lastSent = 0;
    let lastLat = null; let lastLng = null;
    setGpsActivo(true); // solo una vez al iniciar
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        // Solo actualizar estado si la posición cambió significativamente (>5m aprox)
        const latStr = lat.toFixed(5); const lngStr = lng.toFixed(5);
        if (latStr !== lastLat || lngStr !== lastLng) {
          lastLat = latStr; lastLng = lngStr;
          setPosicionActual({ lat: lat.toFixed(6), lng: lng.toFixed(6), precision: Math.round(accuracy) });
        }
        // Enviar al backend máximo cada 10s
        const now = Date.now();
        if (now - lastSent > 10000) {
          lastSent = now;
          apiActualizarPosicionChofer(ordenId, lat.toFixed(6), lng.toFixed(6))
            .then(() => { gpsFailCount.current = 0; })
            .catch(() => {
              gpsFailCount.current = (gpsFailCount.current || 0) + 1;
              if (gpsFailCount.current >= 3) mostrarNotificacion('GPS: sin conexión con el servidor', 'warning');
            });
        }
      },
      (err) => {
        setGpsActivo(false);
        const msg = err.code === 1 ? 'Permiso de ubicación denegado'
          : err.code === 2 ? 'No se pudo obtener la ubicación'
          : 'Tiempo de espera de GPS agotado';
        mostrarNotificacion(`GPS: ${msg}`, 'error');
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    gpsWatchIdsRef.current[ordenId] = id;
  };

  // Detener GPS tracking de una orden específica (no afecta el tracking de otras órdenes en curso)
  const detenerGPS = (ordenId) => {
    const id = gpsWatchIdsRef.current[ordenId];
    if (id != null) {
      navigator.geolocation.clearWatch(id);
      delete gpsWatchIdsRef.current[ordenId];
    }
    if (Object.keys(gpsWatchIdsRef.current).length === 0) setGpsActivo(false);
  };

  // Limpiar todos los GPS watch activos al desmontar el componente
  useEffect(() => {
    return () => {
      Object.values(gpsWatchIdsRef.current).forEach(id => navigator.geolocation?.clearWatch(id));
      gpsWatchIdsRef.current = {};
    };
  }, []);

  // GPS libre — transmitir posición al entrar a la app aunque no haya orden activa
  // Permite que el transportista vea la unidad en Mapa de Unidades en todo momento
  const gpsLibreRef = useRef(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    let lastSent = 0;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        setPosicionActual(prev => {
          const latStr = lat.toFixed(5); const lngStr = lng.toFixed(5);
          if (prev?.lat?.toString().startsWith(latStr) && prev?.lng?.toString().startsWith(lngStr)) return prev;
          return { lat: lat.toFixed(6), lng: lng.toFixed(6), precision: Math.round(accuracy) };
        });
        const now = Date.now();
        if (now - lastSent > 15000) { // cada 15s cuando no hay orden activa
          lastSent = now;
          apiActualizarPosicionLibre(lat.toFixed(6), lng.toFixed(6)).catch(() => {});
        }
      },
      () => {}, // errores silenciosos — el GPS de orden ya muestra mensajes
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
    gpsLibreRef.current = watchId;
    return () => navigator.geolocation.clearWatch(watchId);
  }, []); // eslint-disable-line

  // Compartir ubicación actual por WhatsApp/link
  const compartirUbicacion = () => {
    if (!posicionActual) {
      mostrarNotificacion('GPS no disponible aún', 'error');
      return;
    }
    const url = `https://www.google.com/maps?q=${posicionActual.lat},${posicionActual.lng}`;
    const msg = `Mi ubicación actual: ${url}`;
    if (navigator.share) {
      navigator.share({ title: 'Mi ubicación', url });
    } else {
      navigator.clipboard?.writeText(msg);
      mostrarNotificacion('Link de ubicación copiado al portapapeles', 'success');
    }
  };

  // Mensajes no leídos en todas las órdenes activas
  const totalUnreadMsgs = useMemo(() => {
    const msgs = store.mensajes || [];
    return misOrdenes
      .filter(o => o.viajeId)
      .reduce((acc, o) => {
        const cnt = msgs.filter(m =>
          m.viajeId === o.viajeId &&
          m.emisorId !== usuario?.id &&
          (!m.destinatarioRol || m.destinatarioRol === 'chofer') &&
          m.fecha > (lastSeenMessages[o.viajeId] || '1970')
        ).length;
        return acc + cnt;
      }, 0);
  }, [misOrdenes, store.mensajes, usuario?.id, lastSeenMessages]); // eslint-disable-line

  // ========== MENÚ DE NAVEGACIÓN ==========
  const menu = useMemo(() => [
    { id: 'ordenes', label: 'Mis Órdenes', icon: Package, badge: ordenesAsignadas.length + ordenesEnProgreso.length },
    { id: 'en_curso', label: 'Orden Actual', icon: Navigation, badge: ordenesEnProgreso.length, badgeMsg: totalUnreadMsgs },
    { id: 'historial', label: 'Historial', icon: Clock },
    { id: 'rendimiento', label: 'Mi Desempeño', icon: TrendingUp },
    { id: 'perfil', label: 'Mi Perfil', icon: User }
  ], [ordenesAsignadas.length, ordenesEnProgreso.length, totalUnreadMsgs]); // eslint-disable-line

  // ========== COMPONENTES DE MODALES ==========

  // Modal: Reportar Incidencia
  const [formIncidencia, setFormIncidencia] = useState({ tipo: '', descripcion: '', foto: null });
  const [incidenciaEnvio, setIncidenciaEnvio] = useState(false);

  // Modal: Fotos reales con cámara
  const ModalEvidencia = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-gray-100">Evidencias de entrega</h3>
            <button onClick={() => setMostrarEvidencia(false)} className="text-gray-400 hover:text-gray-200"><X size={24} /></button>
          </div>
          <p className="text-gray-400 text-sm mb-5">Usa la cámara o selecciona desde tu galería.</p>
          <div className="space-y-3">
            {TIPOS_EVIDENCIA.map(({ tipo, label, sub, color }) => {
              const ev = evidenciasSubidas.find(e => e.tipo === tipo);
              return (
                <div key={tipo} className={`w-full p-3 border-2 rounded-xl transition ${ev ? `border-${color}-400 bg-${color}-900/30` : 'border-dashed border-gray-600'}`}>
                  <div className="flex items-center space-x-3">
                    {(ev?.url || ev?.base64)
                      ? <img src={ev.url || ev.base64} alt={label} className="w-14 h-14 rounded-lg object-cover flex-shrink-0 border border-gray-600" />
                      : <div className={`w-14 h-14 bg-${color}-900/40 rounded-xl flex items-center justify-center flex-shrink-0`}><Camera size={26} className={`text-${color}-400`} /></div>
                    }
                    <div className="text-left flex-1">
                      <p className="font-semibold text-gray-100 text-sm">{label}</p>
                      <p className="text-xs text-gray-400">{sub}</p>
                      {ev && <p className="text-xs text-green-400 font-medium mt-0.5">✓ Capturada a las {ev.hora}</p>}
                    </div>
                    {ev && <CheckCircle size={22} className="text-green-500 flex-shrink-0" />}
                  </div>
                  <div className="flex space-x-2 mt-2">
                    <button onClick={() => subirEvidencia(tipo)}
                      className="flex-1 flex items-center justify-center space-x-1.5 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-gray-200 font-medium">
                      <Camera size={15} /><span>Cámara</span>
                    </button>
                    <button onClick={() => subirDesdeGaleria(tipo)}
                      className="flex-1 flex items-center justify-center space-x-1.5 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-gray-200 font-medium">
                      <ImagePlus size={15} /><span>Galería</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-5 p-3 bg-blue-950/40 rounded-lg text-sm text-blue-400">
            <strong>{evidenciasSubidas.length}</strong> de 2 fotos mínimas capturadas
            {evidenciasSubidas.length >= 2 && <span className="ml-2 text-green-400 font-semibold">✓ Listo</span>}
          </div>
          <button onClick={() => setMostrarEvidencia(false)}
            disabled={evidenciasSubidas.length < 2}
            className={`w-full mt-4 px-4 py-3 rounded-lg font-medium ${evidenciasSubidas.length >= 2 ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`}>
            Listo
          </button>
        </div>
      </div>
    </div>
  );

  // Modal: Firma digital táctil del cliente — sin hooks propios (usan refs del padre)
  const ModalFirma = () => {
    const getPos = (e, canvas) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      if (e.touches) {
        return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
      }
      return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
    };

    const startDraw = (e) => {
      e.preventDefault();
      firmaDibujandoRef.current = true;
      firmaLastPos.current = getPos(e, firmaCanvasRef.current);
    };

    const draw = (e) => {
      e.preventDefault();
      if (!firmaDibujandoRef.current || !firmaCanvasRef.current) return;
      const ctx = firmaCanvasRef.current.getContext('2d');
      const pos = getPos(e, firmaCanvasRef.current);
      ctx.beginPath();
      ctx.moveTo(firmaLastPos.current.x, firmaLastPos.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.strokeStyle = '#1d4ed8';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      firmaLastPos.current = pos;
      if (!firmaTieneFirma) setFirmaTieneFirma(true);
    };

    const stopDraw = (e) => { e?.preventDefault(); firmaDibujandoRef.current = false; };

    const limpiar = () => {
      const canvas = firmaCanvasRef.current;
      if (!canvas) return;
      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
      setFirmaTieneFirma(false);
    };

    const confirmar = () => {
      if (!firmaTieneFirma) return;
      const dataUrl = firmaCanvasRef.current.toDataURL('image/png');
      confirmarFirma(dataUrl);
      setFirmaTieneFirma(false);
    };

    return (
      <div style={{ position:'fixed', inset:0, zIndex:50, background:'#111827', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', background:'#1f2937', borderBottom:'1px solid #374151', flexShrink:0 }}>
          <div>
            <h3 style={{ color:'#f3f4f6', fontWeight:700, fontSize:'1.1rem', margin:0 }}>✍️ Firma del Cliente</h3>
            <p style={{ color:'#9ca3af', fontSize:'0.75rem', margin:0 }}>Firma para confirmar recepción de mercancía</p>
          </div>
          <button onClick={() => setMostrarFirma(false)} style={{ color:'#9ca3af', background:'none', border:'none', cursor:'pointer', padding:4 }}><X size={22} /></button>
        </div>

        {/* Canvas — ocupa todo el espacio disponible */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', padding:'12px 12px 8px 12px', gap:8, minHeight:0 }}>
          <div style={{ flex:1, position:'relative', touchAction:'none', borderRadius:12, overflow:'hidden', border:'2px solid #374151', background:'#ffffff', minHeight:150 }}>
            <canvas
              ref={firmaCanvasRef}
              width={900} height={1200}
              style={{ width:'100%', height:'100%', display:'block', cursor:'crosshair', background:'#ffffff' }}
              onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
            />
            {!firmaTieneFirma && (
              <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none' }}>
                <p style={{ color:'#9ca3af', fontSize:'1.1rem', fontWeight:500, userSelect:'none' }}>Firma aquí</p>
              </div>
            )}
          </div>

          {/* Aviso legal */}
          <div style={{ background:'rgba(66,32,6,0.6)', border:'1px solid #92400e', borderRadius:8, padding:'8px 12px', flexShrink:0 }}>
            <p style={{ color:'#fde68a', fontSize:'0.75rem', margin:0 }}>
              <AlertCircle size={13} style={{ display:'inline', marginRight:6, verticalAlign:'middle' }} />
              Al firmar, el cliente confirma haber recibido la mercancía en buenas condiciones.
            </p>
          </div>
        </div>

        {/* Footer con botones */}
        <div style={{ flexShrink:0, background:'#1f2937', borderTop:'1px solid #374151', padding:'12px 16px', display:'flex', gap:10 }}>
          <button onClick={limpiar} style={{ flex:1, padding:'12px 0', border:'1px solid #4b5563', borderRadius:10, background:'none', color:'#e5e7eb', fontWeight:500, cursor:'pointer', fontSize:'0.95rem' }}>
            Limpiar
          </button>
          <button onClick={confirmar} disabled={!firmaTieneFirma}
            style={{ flex:2, padding:'12px 0', borderRadius:10, border:'none', fontWeight:600, cursor: firmaTieneFirma ? 'pointer' : 'not-allowed', background: firmaTieneFirma ? '#16a34a' : '#4b5563', color: firmaTieneFirma ? '#ffffff' : '#9ca3af', display:'flex', alignItems:'center', justifyContent:'center', gap:8, fontSize:'0.95rem' }}>
            <CheckCircle size={18} /><span>Confirmar Firma</span>
          </button>
        </div>
      </div>
    );
  };

  // Modal: Resumen de entrega antes de confirmar
  const ModalResumen = () => (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm max-h-[92vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-100">Resumen de entrega</h3>
            <button onClick={() => setMostrarResumen(false)} className="text-gray-400 hover:text-gray-200"><X size={22} /></button>
          </div>

          {/* Orden */}
          <div className="bg-gray-900 rounded-xl p-3 mb-3">
            <p className="text-xs text-gray-400 mb-1">Orden</p>
            <p className="font-bold text-gray-100">{ordenEnCurso?.id}</p>
            <p className="text-sm text-gray-300">{ordenEnCurso?.origen} → {ordenEnCurso?.destino}</p>
            <p className="text-sm text-gray-300">Cliente: <span className="font-medium">{ordenEnCurso?.cliente}</span></p>
          </div>

          {/* Fotos */}
          <div className="mb-3">
            <p className="text-sm font-semibold text-gray-200 mb-2">📸 Evidencias ({evidenciasSubidas.length})</p>
            <div className="flex gap-2 flex-wrap">
              {evidenciasSubidas.map(ev => (
                <div key={ev.id} className="relative">
                  {(ev.url || ev.base64)
                    ? <img src={ev.url || ev.base64} alt={ev.nombre} className="w-20 h-20 rounded-lg object-cover border border-gray-600" />
                    : <div className="w-20 h-20 bg-gray-900 rounded-lg flex items-center justify-center"><Camera size={20} className="text-gray-400" /></div>
                  }
                  <p className="text-xs text-center text-gray-400 mt-0.5 w-20 truncate">{ev.nombre}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Firma */}
          <div className="mb-3">
            <p className="text-sm font-semibold text-gray-200 mb-2">✍️ Firma del cliente</p>
            {firmaData
              ? <img src={firmaData} alt="Firma" className="w-full h-24 object-contain border-2 border-blue-300 rounded-lg bg-white p-2" />
              : firmaCliente
                ? <p className="text-sm text-green-400">✓ Confirmada</p>
                : <p className="text-sm text-gray-400">No obtenida</p>
            }
          </div>

          {/* GPS */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-200 mb-1">📍 Ubicación de entrega</p>
            {entregaGPS
              ? <p className="text-sm text-gray-300">{entregaGPS.lat}, {entregaGPS.lng} <span className="text-gray-400">(±{entregaGPS.precision}m)</span></p>
              : <p className="text-sm text-gray-400">No disponible</p>
            }
            <p className="text-xs text-gray-400 mt-0.5">{new Date().toLocaleString('es-MX')}</p>
          </div>

          <div className="bg-green-950/40 border border-green-800 rounded-lg p-3 mb-4">
            <p className="text-sm text-green-300 font-medium">✅ Todo listo para confirmar la entrega.</p>
            <p className="text-xs text-green-400 mt-0.5">Se notificará al cliente y transportista.</p>
          </div>

          <div className="flex space-x-3">
            <button onClick={() => setMostrarResumen(false)} className="flex-1 px-4 py-2.5 border border-gray-600 rounded-lg hover:bg-gray-700 font-medium text-sm text-gray-200">Revisar</button>
            <button onClick={confirmarEntregaFinal}
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-xl font-bold flex items-center justify-center space-x-2 hover:bg-green-700">
              <CheckCircle size={18} /><span>Confirmar Entrega</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Modal: Detalle de Orden
  const ModalDetalleOrden = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <div className="flex items-center space-x-3">
                <h3 className="text-xl font-bold text-gray-100">{mostrarDetalleOrden?.id}</h3>
                {mostrarDetalleOrden?.prioridad && (
                  <span className={`px-2 py-1 text-xs font-semibold rounded ${getPrioridadColor(mostrarDetalleOrden.prioridad)}`}>
                    {mostrarDetalleOrden.prioridad.toUpperCase()}
                  </span>
                )}
              </div>
              <span className={`mt-2 inline-block px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(mostrarDetalleOrden?.status)}`}>
                {getStatusText(mostrarDetalleOrden?.status)}
              </span>
            </div>
            <button onClick={() => setMostrarDetalleOrden(null)} className="text-gray-400 hover:text-gray-200">
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            {/* Cliente */}
            <div className="bg-gray-900 rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-1">Cliente</p>
              <p className="font-semibold text-lg text-gray-100">{mostrarDetalleOrden?.cliente}</p>
              <p className="text-sm text-gray-300">{mostrarDetalleOrden?.contactoCliente}</p>
              <button
                onClick={() => llamarCliente(mostrarDetalleOrden?.telefonoCliente)}
                className="mt-2 text-blue-400 text-sm flex items-center hover:underline"
              >
                <Phone size={14} className="mr-1" /> {mostrarDetalleOrden?.telefonoCliente}
              </button>
            </div>

            {/* Ruta */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-950/40 rounded-xl p-4">
                <p className="text-xs text-green-400 font-medium mb-1">📍 Origen</p>
                <p className="font-semibold text-gray-100">{mostrarDetalleOrden?.origen}</p>
                <p className="text-xs text-gray-300 mt-1">{mostrarDetalleOrden?.origenDir}</p>
              </div>
              <div className="bg-red-950/40 rounded-xl p-4">
                <p className="text-xs text-red-400 font-medium mb-1">🏁 Destino</p>
                <p className="font-semibold text-gray-100">{mostrarDetalleOrden?.destino}</p>
                <p className="text-xs text-gray-300 mt-1">{mostrarDetalleOrden?.destinoDir}</p>
              </div>
            </div>

            {/* Detalles de carga */}
            <div className="bg-blue-950/40 rounded-xl p-4">
              <p className="text-xs text-blue-400 font-medium mb-2">📦 Detalles de Carga</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-400">Carga:</span> <span className="font-medium text-gray-200">{mostrarDetalleOrden?.carga || mostrarDetalleOrden?.instrucciones || '—'}</span></div>
                <div><span className="text-gray-400">Peso:</span> <span className="font-medium text-gray-200">{mostrarDetalleOrden?.peso ? `${parseFloat(mostrarDetalleOrden.peso) || mostrarDetalleOrden.peso} tons` : '—'}</span></div>
                <div><span className="text-gray-400">Tipo unidad:</span> <span className="font-medium text-gray-200">{mostrarDetalleOrden?.tipoUnidad || mostrarDetalleOrden?.unidadLabel || '—'}</span></div>
                <div><span className="text-gray-400">Unidad:</span> <span className="font-medium font-mono text-gray-200">{mostrarDetalleOrden?.unidadLabel || '—'}</span></div>
              </div>
            </div>

            {/* Tiempo estimado */}
            {mostrarDetalleOrden?.tiempoEstimado && (
              <div className="bg-gray-900 rounded-xl p-3 text-center">
                <p className="text-xs text-gray-400">Tiempo estimado de entrega</p>
                <p className="font-bold text-lg text-gray-100">{mostrarDetalleOrden.tiempoEstimado}</p>
              </div>
            )}

            {/* Instrucciones */}
            {mostrarDetalleOrden?.instrucciones && (
              <div className="bg-yellow-950/40 border border-yellow-800 rounded-xl p-4">
                <p className="text-xs text-yellow-400 font-semibold mb-1">⚠️ Instrucciones Especiales</p>
                <p className="text-sm text-yellow-300">{mostrarDetalleOrden?.instrucciones}</p>
              </div>
            )}

            {/* Fecha de recogida */}
            {mostrarDetalleOrden?.fechaRecogida && (
              <div className="bg-purple-950/40 rounded-xl p-4">
                <p className="text-xs text-purple-400 font-medium mb-1">📅 Fecha de Recogida</p>
                <p className="font-semibold text-gray-100">{mostrarDetalleOrden.fechaRecogida}</p>
              </div>
            )}
          </div>

          {/* Botones de acción */}
          {(mostrarDetalleOrden?.status === 'asignada' || mostrarDetalleOrden?.status === 'aceptada') && (
            <div className="flex space-x-4 mt-6">
              <button 
                onClick={() => abrirNavegacion(mostrarDetalleOrden, 'ruta')}
                className="flex-1 px-4 py-3 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 font-medium flex items-center justify-center space-x-2"
              >
                <Navigation size={18} />
                <span>Ver Ruta</span>
              </button>
              <button 
                onClick={() => { iniciarOrden(mostrarDetalleOrden); setMostrarDetalleOrden(null); }}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center space-x-2"
              >
                <Play size={18} />
                <span>Iniciar</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Componente de Notificación
  // ========== RENDER PRINCIPAL ==========
  return (
    <div className="flex h-screen bg-gray-950 overflow-hidden">
      {/* Banner de conexión — solo visible cuando no hay señal o se está sincronizando */}
      {(!isOnline || syncing) && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 99998 }}
          className={`flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium ${syncing ? 'bg-blue-600 text-white' : 'bg-gray-800 text-yellow-300 border-t border-yellow-600'}`}>
          {syncing
            ? <><RefreshCw size={14} className="animate-spin" /><span>Sincronizando {queueSize} paso{queueSize !== 1 ? 's' : ''}...</span></>
            : <><WifiOff size={14} /><span>Sin conexión{queueSize > 0 ? ` — ${queueSize} paso${queueSize !== 1 ? 's' : ''} pendiente${queueSize !== 1 ? 's' : ''}` : ''}</span></>
          }
        </div>
      )}
      {toastNotif.length > 0 && !toastPausado && (() => {
        const notif = toastNotif[0];
        const esAlerta = notif.tipo?.startsWith('alerta_');
        const alertaTipo = esAlerta ? notif.tipo.replace('alerta_', '') : null;
        // Alertas simples no persistentes → píldora clásica
        if (esAlerta && !notif.persistente) {
          const bgColor = { success: 'bg-green-500', warning: 'bg-yellow-500', error: 'bg-red-500' }[alertaTipo] || 'bg-gray-700';
          return (
            <div className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${bgColor} text-white px-8 py-5 rounded-2xl shadow-2xl z-[99999] flex items-center space-x-3 text-center max-w-sm w-full mx-4 pointer-events-none`}>
              {alertaTipo === 'success' && <CheckCircle size={24} className="flex-shrink-0" />}
              {alertaTipo === 'warning' && <AlertCircle size={24} className="flex-shrink-0" />}
              {alertaTipo === 'error' && <XCircle size={24} className="flex-shrink-0" />}
              <span className="font-medium">{notif.mensaje}</span>
            </div>
          );
        }
        const headerColor = esAlerta
          ? { success: 'bg-green-600', warning: 'bg-yellow-500', error: 'bg-red-600' }[alertaTipo] || 'bg-gray-700'
          : 'bg-orange-500';
        const borderColor = esAlerta
          ? { success: 'border-green-800', warning: 'border-yellow-700', error: 'border-red-800' }[alertaTipo] || 'border-gray-700'
          : 'border-orange-800';
        const hoverColor = esAlerta
          ? { success: 'hover:bg-green-950/40', warning: 'hover:bg-yellow-950/40', error: 'hover:bg-red-950/40' }[alertaTipo] || 'hover:bg-gray-700'
          : 'hover:bg-orange-900/30';
        const tocaColor = esAlerta
          ? { warning: 'text-yellow-400', error: 'text-red-400' }[alertaTipo]
          : 'text-orange-400';
        const seccionDestino = esAlerta ? (notif.navegar || null) : ({
          orden_asignada: 'ordenes',
          orden_actualizada: 'ordenes',
          nueva_orden: 'ordenes',
        }[notif.tipo] || null);
        const esNavegable = !!seccionDestino || notif.tipo === 'chat_mensaje';
        return (
          <div className="fixed inset-0 flex items-center justify-center z-[2000] pointer-events-none">
            <div className={`bg-gray-800 border ${borderColor} rounded-2xl shadow-2xl w-96 pointer-events-auto animate-slide-in-right`}>
              <div className={`${headerColor} px-5 py-3 rounded-t-2xl flex items-center justify-between`}>
                <div className="flex items-center gap-2 text-white text-sm font-semibold">
                  {esAlerta
                    ? (alertaTipo === 'success' ? <CheckCircle size={16} /> : alertaTipo === 'error' ? <XCircle size={16} /> : <AlertCircle size={16} />)
                    : <Bell size={16} />}
                  <span>{notif.titulo || 'Nueva notificación'}{toastNotif.length > 1 ? ` (1 de ${toastNotif.length})` : ''}</span>
                </div>
                <button onClick={() => cerrarToast()} className="text-white hover:opacity-70">
                  <X size={18} />
                </button>
              </div>
              <button
                type="button"
                className={`w-full text-left p-5 select-none ${esNavegable ? `cursor-pointer ${hoverColor}` : ''}`}
                onClick={() => {
                  if (notif.tipo === 'chat_mensaje') {
                    const viajeId = notif.datos?.viajeId || ordenEnCurso?.viajeId;
                    if (viajeId) setChatModal({ viajeId, contraparte: notif.contraparte || { nombre: 'Transportista', rol: 'transportista' } });
                    cerrarToast(); return;
                  }
                  if (esAlerta && notif.navegar) { setSeccion(notif.navegar); cerrarToast(); return; }
                  if (notif.tipo === 'orden_asignada' && notif.datos?.ordenId) {
                    const orden = misOrdenes.find(o => o.id === notif.datos.ordenId);
                    if (orden) { setSeccion('ordenes'); setMostrarDetalleOrden(orden); cerrarToast(); return; }
                  }
                  if (seccionDestino) { setSeccion(seccionDestino); setMostrarNotificaciones(false); }
                  cerrarToast();
                }}
              >
                <p className="font-bold text-gray-100">{notif.titulo}</p>
                <p className="text-gray-300 text-sm mt-1">{notif.mensaje}</p>
                {notif.fecha && (
                  <p className="text-gray-400 text-xs mt-2">
                    {new Date(notif.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                    {' · '}
                    {new Date(notif.fecha).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
                {esNavegable && tocaColor && (
                  <p className={`${tocaColor} text-xs font-semibold mt-3`}>
                    {notif.tipo === 'chat_mensaje' ? 'Toca para abrir chat →' : 'Toca para ver detalles →'}
                  </p>
                )}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Panel de notificaciones */}
      {mostrarNotificaciones && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-end" onClick={() => setMostrarNotificaciones(false)}>
          <div className="w-80 bg-gray-800 h-full shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex justify-between items-center bg-gray-900">
              <h3 className="font-bold text-white">Notificaciones</h3>
              <div className="flex items-center gap-3">
                {misNotificaciones.length > 0 && (
                  <button onClick={() => { apiEliminarTodasNotificaciones().catch(() => {}); limpiarNotificaciones(usuario?.id, 'chofer'); setMostrarNotificaciones(false); }} className="text-xs text-red-400 hover:text-red-300 font-medium">Limpiar todo</button>
                )}
                <button onClick={() => setMostrarNotificaciones(false)} className="text-gray-300 hover:text-white"><X size={20} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto pb-16 md:pb-0">
              {misNotificaciones.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <Bell size={40} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Sin notificaciones</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-700">
                  {misNotificacionesOrdenadas.map(n => {
                      const destino = n.tipo === 'orden_asignada' ? 'ordenes' : null;
                      const linkLabel = n.tipo === 'orden_asignada' ? 'Ver orden →' : null;
                      return (
                        <div
                          key={n.id}
                          className={`p-4 ${destino ? 'cursor-pointer hover:bg-gray-700' : ''} ${n.leida ? 'bg-gray-800' : 'bg-orange-900/30'}`}
                          onClick={() => { if (destino) { setSeccion(destino); setMostrarNotificaciones(false); } }}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${n.leida ? 'bg-gray-600' : 'bg-orange-500'}`} />
                            <div className="flex-1">
                              <p className="font-semibold text-gray-100 text-sm">{n.titulo}</p>
                              <p className="text-gray-300 text-sm mt-0.5">{n.mensaje}</p>
                              <p className="text-xs text-gray-400 mt-1">{new Date(n.creadoEn).toLocaleString('es-MX')}</p>
                              {linkLabel && <span className="mt-1 inline-block text-xs text-orange-400 font-semibold">{linkLabel}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Overlay móvil */}
      {sidebarAbierto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setSidebarAbierto(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-30 w-72 bg-gray-900 text-white p-4 pb-20 md:pb-4 flex flex-col overflow-y-auto
        transform transition-transform duration-300 ease-in-out
        ${sidebarAbierto ? 'translate-x-0' : '-translate-x-full'}
        md:relative md:translate-x-0 md:w-64 md:z-auto
      `}>
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-black text-orange-400 leading-none">Choyero!</h1>
            <p className="text-xs font-semibold text-gray-400 tracking-widest uppercase mt-0.5">Marketplace Logístico</p>
            <div className="mt-2 px-2 py-1 bg-orange-600 rounded text-xs font-semibold inline-block">
              CHOFER
            </div>
          </div>
          <button
            onClick={abrirNotificaciones}
            className="relative mt-1 p-2 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Bell size={20} className="text-gray-300" />
            {notifsPendientes.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {notifsPendientes.length}
              </span>
            )}
          </button>
        </div>
        
        <nav className="space-y-2 flex-1">
          {menu.map(item => (
            <button
              key={item.id}
              onClick={() => { setSeccion(item.id); setSidebarAbierto(false); }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition ${
                seccion === item.id ? 'bg-orange-600' : 'hover:bg-gray-800'
              }`}
            >
              <item.icon size={20} />
              <span className="text-sm">{item.label}</span>
              <span className="ml-auto flex items-center gap-1">
                {item.badge > 0 && (
                  <span className={`text-xs px-2 py-1 rounded-full ${item.id === 'en_curso' ? 'bg-green-500' : 'bg-orange-500'}`}>
                    {item.badge}
                  </span>
                )}
                {item.badgeMsg > 0 && (
                  <span className="text-xs px-2 py-1 rounded-full bg-red-500 flex items-center gap-0.5">
                    <MessageCircle size={11} />{item.badgeMsg}
                  </span>
                )}
              </span>
            </button>
          ))}
        </nav>

        {/* Info del chofer */}
        <div className="p-4 bg-gray-800 rounded-xl">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center font-bold text-lg">
              {choferInfo.nombre.split(' ').map(n => n[0]).slice(0, 2).join('')}
            </div>
            <div>
              <p className="font-semibold text-sm">{choferInfo.nombre}</p>
              <p className="text-xs text-gray-400">{choferInfo.empresa}</p>
            </div>
          </div>
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-1">
              {choferInfo.viajesCompletados > 0 ? (
                <><Star size={14} className="text-yellow-400" fill="currentColor" /><span>{choferInfo.calificacion}</span></>
              ) : (
                <span className="text-gray-400">Sin calificación</span>
              )}
            </div>
            <span className="text-gray-400">{choferInfo.viajesCompletados} entregas</span>
          </div>
        </div>

        {/* Botón cerrar sesión */}
        <button
          onClick={onLogout}
          className="mt-4 flex items-center space-x-2 px-4 py-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition"
        >
          <LogOut size={18} />
          <span className="text-sm">Cerrar Sesión</span>
        </button>
      </div>

      {/* Contenido Principal */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header móvil */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 bg-gray-900 text-white flex-shrink-0">
          <button onClick={() => setSidebarAbierto(true)} className="p-2 rounded-lg hover:bg-gray-800">
            <Menu size={22} />
          </button>
          <span className="font-black text-orange-400 text-lg">Choyero!</span>
          <button onClick={abrirNotificaciones} className="relative p-2 rounded-lg hover:bg-gray-800">
            <Bell size={22} className="text-gray-300" />
            {notifsPendientes.length > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {notifsPendientes.length}
              </span>
            )}
          </button>
        </div>
      <div className="flex-1 p-4 pb-24 md:p-8 overflow-auto bg-gray-950">
        
        {/* ========== MIS ÓRDENES ========== */}
        {seccion === 'ordenes' && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-3xl font-bold text-gray-100">Mis Órdenes de Trabajo</h2>
              {ordenesAsignadas.length > 0 && (
                <div className="flex items-center space-x-2 bg-orange-900/40 px-4 py-2 rounded-lg">
                  <Bell size={18} className="text-orange-400" />
                  <span className="text-orange-200 font-medium text-sm">{ordenesAsignadas.length} pendientes</span>
                </div>
              )}
            </div>
            <p className="text-gray-500 text-sm mb-4">Órdenes asignadas por tu empresa. Haz clic en una fila para ver los detalles.</p>

            {/* Widget calificación chofer */}
            {choferInfo.viajesCompletados > 0 && (
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 mb-5 flex items-center gap-4">
                <div className="w-11 h-11 bg-yellow-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Star size={22} className="text-yellow-400" fill="currentColor" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Mi calificación</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-black text-yellow-400">{choferInfo.calificacion || '—'}</span>
                    <span className="text-sm text-gray-500">/ 5</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-gray-400">{choferInfo.viajesCompletados} entrega{choferInfo.viajesCompletados !== 1 ? 's' : ''}</p>
                  <div className="flex items-center gap-0.5 justify-end mt-1">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} size={11}
                        className={i <= Math.round(choferInfo.calificacion || 0) ? 'text-yellow-400' : 'text-gray-600'}
                        fill={i <= Math.round(choferInfo.calificacion || 0) ? 'currentColor' : 'none'} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {misOrdenes.length === 0 ? (
              <div className="bg-gray-800 rounded-xl shadow p-12 text-center">
                <Package size={64} className="mx-auto text-gray-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-100 mb-2">No hay órdenes asignadas</h3>
                <p className="text-gray-400">Cuando tu empresa te asigne una orden, aparecerá aquí</p>
              </div>
            ) : (
              <div className="space-y-3">
                {misOrdenes.map(orden => {
                  const isExpanded = expandedOrden === orden.id;
                  const enProgreso = STATUS_EN_PROGRESO.includes(orden.status);
                  const idCorto = orden.id?.split('-')[0]?.toUpperCase() || orden.id?.slice(0, 8)?.toUpperCase();

                  return (
                    <div key={orden.id} className={`bg-gray-800 rounded-xl shadow-sm border-l-4 overflow-hidden ${enProgreso ? 'border-orange-500' : 'border-gray-700'}`}>
                      {/* Cabecera de la tarjeta */}
                      <div
                        onClick={() => setExpandedOrden(isExpanded ? null : orden.id)}
                        className={`p-4 cursor-pointer transition-colors ${isExpanded ? 'bg-orange-900/30' : enProgreso ? 'bg-orange-900/20' : 'bg-gray-800 hover:bg-gray-700'}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono font-bold text-gray-100 text-sm">{idCorto}</span>
                              {orden.prioridad && orden.prioridad !== 'normal' && (
                                <span className={`px-1.5 py-0.5 text-xs font-bold rounded ${getPrioridadColor(orden.prioridad)}`}>{orden.prioridad.toUpperCase()}</span>
                              )}
                              {enProgreso && <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse flex-shrink-0" />}
                            </div>
                            <p className="text-xs text-gray-400 truncate mt-0.5">{orden.cliente}</p>
                            <p className="text-sm text-gray-200 mt-1">
                              <span className="font-medium">{orden.origen?.split(',')[0]}</span>
                              <span className="text-gray-400 mx-1">→</span>
                              <span className="font-medium">{orden.destino?.split(',')[0]}</span>
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${getStatusColor(orden.status)}`}>
                              {getStatusText(orden.status)}
                            </span>
                            <ChevronDown size={16} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                        {(orden.carga || orden.fechaRecogida) && (
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center gap-3 text-xs text-gray-400">
                              {orden.carga && <span className="truncate max-w-[140px]">{orden.carga}</span>}
                              {orden.peso && <span>{parseFloat(orden.peso) || orden.peso} tons</span>}
                            </div>
                            {orden.fechaRecogida && (() => {
                              const fmtH = (h) => { try { return new Date(`1970-01-01T${h}`).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: true }); } catch { return h; } };
                              const horaInicio = orden.horaRecogida && orden.horaRecogida !== '00:00' ? fmtH(orden.horaRecogida) : null;
                              const horaFin = orden.horaFinCarga ? fmtH(orden.horaFinCarga) : null;
                              return (
                                <div className="flex items-center gap-1.5 bg-orange-900/40 border border-orange-700/50 rounded-lg px-2.5 py-1.5">
                                  <span className="text-sm">🕐</span>
                                  <div className="text-xs">
                                    <span className="text-orange-300 font-semibold">Cliente disponible: </span>
                                    {horaInicio
                                      ? <span className="text-white font-bold">{horaInicio}{horaFin ? ` – ${horaFin}` : ''}</span>
                                      : <span className="text-gray-400">Horario no especificado</span>
                                    }
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Panel expandible */}
                      {isExpanded && (
                        <div className="border-t border-orange-900/50 bg-orange-950/30 p-4 space-y-4">
                          {/* Ruta detallada */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-green-950/40 rounded-xl p-3 border border-green-800">
                              <p className="text-xs text-green-400 font-semibold mb-1">📍 Origen</p>
  <p className="font-semibold text-gray-100 text-sm">{orden.origen}</p>
                              {orden.origenDir && <p className="text-xs text-gray-400 mt-0.5">{orden.origenDir}</p>}
                            </div>
                            <div className="bg-red-950/40 rounded-xl p-3 border border-red-800">
                              <p className="text-xs text-red-400 font-semibold mb-1">🏁 Destino</p>
                              <p className="font-semibold text-gray-100 text-sm">{orden.destino}</p>
                              {orden.destinoDir && <p className="text-xs text-gray-400 mt-0.5">{orden.destinoDir}</p>}
                            </div>
                          </div>

                          {orden.instrucciones && (
                            <div className="bg-blue-950/40 border border-blue-800 rounded-xl p-3">
                              <p className="text-xs font-semibold text-blue-400 mb-1">Instrucciones</p>
                              <p className="text-sm text-blue-300">{orden.instrucciones}</p>
                            </div>
                          )}

                          {/* Botones de acción */}
                          <div className="grid grid-cols-2 gap-2">
                            {/* Chat siempre visible si hay empresaId */}
                            {orden.empresaId && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const nombre = orden.empresaNombre
                                    || usuario?.empresa
                                    || 'Transportista';
                                  setChatModal({ viajeId: orden.viajeId || orden.id, contraparte: { id: orden.empresaId, nombre, rol: 'transportista' } });
                                }}
                                className="col-span-2 px-4 py-2.5 bg-purple-950/40 border border-purple-800 text-purple-400 rounded-xl font-medium text-sm flex items-center justify-center gap-2"
                              >
                                <MessageCircle size={16} />
                                <span>Chat con empresa</span>
                              </button>
                            )}
                            {(orden.status === 'asignada' || orden.status === 'aceptada') && (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); abrirNavegacion(orden, 'ruta'); }}
                                  className="px-4 py-2.5 bg-gray-800 border border-gray-600 text-gray-200 rounded-xl font-medium text-sm flex items-center justify-center gap-2"
                                >
                                  <Navigation size={15} />
                                  <span>Ver Ruta</span>
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); iniciarOrden(orden); }}
                                  className="px-4 py-2.5 bg-green-600 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2"
                                >
                                  <Play size={15} />
                                  <span>Iniciar</span>
                                </button>
                              </>
                            )}
                            {enProgreso && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setOrdenActivaId(orden.id); setSeccion('en_curso'); }}
                                className="px-4 py-2.5 bg-orange-600 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2"
                              >
                                <Play size={15} />
                                <span>Continuar</span>
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); setMostrarDetalleOrden(orden); }}
                              className="px-4 py-2.5 bg-gray-800 border border-gray-600 text-gray-200 rounded-xl font-medium text-sm flex items-center justify-center gap-2"
                            >
                              <FileText size={15} />
                              <span>Ver Detalle</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ========== ORDEN EN CURSO ========== */}
        {seccion === 'en_curso' && (
          <div>
            <h2 className="text-3xl font-bold text-gray-100 mb-2">Orden en Curso</h2>

            {/* Selector multi-orden (visible si hay más de una activa) */}
            {ordenesEnProgreso.length > 1 && (
              <div className="mb-4 flex gap-2 flex-wrap">
                {ordenesEnProgreso.map(o => (
                  <button
                    key={o.id}
                    onClick={() => cambiarOrdenActiva(o)}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition ${
                      o.id === (ordenActivaId || ordenesEnProgreso[0]?.id)
                        ? 'bg-orange-600 text-white border-orange-600'
                        : 'bg-gray-800 text-gray-200 border-gray-600 hover:border-orange-400'
                    }`}
                  >
                    {o.id} · {o.origen?.split(',')[0]} → {o.destino?.split(',')[0]}
                  </button>
                ))}
              </div>
            )}

            {ordenesEnProgreso.length <= 1 && (
              <p className="text-gray-400 mb-6">Gestiona tu orden activa</p>
            )}

            {ordenEnCurso ? (
              <div className="space-y-6">
                {/* Tarjeta principal */}
                <div className="bg-gray-800 rounded-xl shadow-lg p-6">
                  <div className="mb-5">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-mono text-lg font-bold text-gray-100">
                        {ordenEnCurso.id?.split('-')[0]?.toUpperCase()}
                      </span>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(ordenEnCurso.status)}`}>
                        {getStatusText(ordenEnCurso.status)}
                      </span>
                    </div>
                    <p className="text-gray-300 text-sm mt-1">{ordenEnCurso.cliente}</p>
                    {ordenEnCurso.horaInicio && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Iniciado {ordenEnCurso.horaInicio}
                        {tiempoTranscurrido && <span className="text-orange-400 font-semibold ml-2">⏱ {tiempoTranscurrido}</span>}
                      </p>
                    )}
                  </div>

                  {/* Alerta de retraso */}
                  {(() => {
                    if (!ordenEnCurso.fechaInicioTs || !ordenEnCurso.tiempoEstimado) return null;
                    const eta = ordenEnCurso.tiempoEstimado;
                    let mins = 0;
                    const hM = eta.match(/(\d+)\s*h/); const mM = eta.match(/(\d+)\s*min/); const dM = eta.match(/(\d+)\s*d/);
                    if (hM) mins += parseInt(hM[1]) * 60;
                    if (mM) mins += parseInt(mM[1]);
                    if (dM) mins += parseInt(dM[1]) * 1440;
                    if (mins === 0) return null;
                    const elapsedMins = (Date.now() - ordenEnCurso.fechaInicioTs) / 60000;
                    if (elapsedMins <= mins * 1.1) return null;
                    const exceso = Math.round(elapsedMins - mins);
                    return (
                      <div className="mb-4 flex items-center gap-2 bg-red-950/40 border border-red-800 rounded-xl px-4 py-3 text-red-400 text-sm font-medium">
                        <AlertCircle size={18} className="flex-shrink-0" />
                        <span>Retraso estimado: {exceso < 60 ? `${exceso} min` : `${Math.floor(exceso/60)}h ${exceso%60}m`} sobre el tiempo previsto</span>
                      </div>
                    );
                  })()}

                  {/* Barra de progreso */}
                  <div className="mb-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-300">Progreso</span>
                      <span className="font-bold text-gray-100">{ordenEnCurso.progreso ?? 0}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-4">
                      <div 
                        className="bg-gradient-to-r from-orange-500 to-orange-600 h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                        style={{ width: `${ordenEnCurso.progreso ?? 0}%` }}
                      >
                        {ordenEnCurso.progreso > 15 && <Truck size={12} className="text-white" />}
                      </div>
                    </div>
                  </div>

                  {/* Checkpoints */}
                  <div className="bg-gray-900 rounded-xl p-4 mb-6">
                    <p className="text-sm font-semibold text-gray-200 mb-3">📍 Checkpoints</p>
                    <div className="space-y-3">
                      {(ordenEnCurso.checkpoints || []).map((cp, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${cp.completado ? 'bg-green-500' : 'bg-gray-600'}`}>
                            {cp.completado && <CheckCircle size={14} className="text-white" />}
                          </div>
                          <span className="flex-1 text-sm text-gray-200">{cp.paso}</span>
                          <span className="text-xs text-gray-400">{cp.hora}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Info de ruta */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-green-950/40 rounded-xl p-4">
                      <p className="text-xs text-green-400 font-medium mb-1">📍 Origen</p>
                      <p className="font-semibold text-gray-100">{ordenEnCurso.origen}</p>
                      {ordenEnCurso.origenDir && <p className="text-xs text-gray-300 mt-1">{ordenEnCurso.origenDir}</p>}
                    </div>
                    <div className="bg-red-950/40 rounded-xl p-4">
                      <p className="text-xs text-red-400 font-medium mb-1">🏁 Destino</p>
                      <p className="font-semibold text-gray-100">{ordenEnCurso.destino}</p>
                      {ordenEnCurso.destinoDir && <p className="text-xs text-gray-300 mt-1">{ordenEnCurso.destinoDir}</p>}
                    </div>
                  </div>

                  {/* Info adicional */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-900 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-400">Carga</p>
                      <p className="font-semibold text-sm text-gray-100">{ordenEnCurso.carga}</p>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-400">Peso</p>
                      <p className="font-semibold text-sm text-gray-100">{ordenEnCurso.peso ? `${parseFloat(ordenEnCurso.peso) || ordenEnCurso.peso} tons` : '—'}</p>
                    </div>
                    <div className="bg-gray-900 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-400">Distancia</p>
                      <p className="font-semibold text-sm text-gray-100">
                        {calcularDistanciaKm(
                          typeof ordenEnCurso.origenCoords === 'string' ? (() => { try { return JSON.parse(ordenEnCurso.origenCoords); } catch { return null; } })() : ordenEnCurso.origenCoords,
                          typeof ordenEnCurso.destinoCoords === 'string' ? (() => { try { return JSON.parse(ordenEnCurso.destinoCoords); } catch { return null; } })() : ordenEnCurso.destinoCoords
                        ) || '—'}
                      </p>
                    </div>
                  </div>

                  {/* GPS Status */}
                  {gpsActivo && posicionActual && (
                    <div className="flex items-center justify-between bg-green-950/40 border border-green-800 rounded-xl px-4 py-2 mb-2">
                      <div className="flex items-center gap-2 text-green-400 text-sm font-semibold">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                        GPS activo · ±{posicionActual.precision}m
                      </div>
                    </div>
                  )}

                  {/* Botones de contacto */}
                  <div className="grid grid-cols-1 gap-3">
                    {ordenEnCurso.telefonoCliente && (
                      <button
                        onClick={() => llamarCliente(ordenEnCurso.telefonoCliente)}
                        className="px-4 py-3 bg-blue-950/40 text-blue-400 border border-blue-800 rounded-xl hover:bg-blue-900/40 flex items-center justify-center space-x-2 font-medium"
                      >
                        <Phone size={20} />
                        <span>Llamar al cliente · {ordenEnCurso.telefonoCliente}</span>
                      </button>
                    )}
                    <button
                      onClick={() => { setOrdenActiva(ordenEnCurso); setMostrarIncidencia(true); }}
                      className="px-4 py-3 bg-orange-900/40 text-orange-400 rounded-xl hover:bg-orange-900/60 flex items-center justify-center space-x-2 font-medium"
                    >
                      <AlertCircle size={20} />
                      <span>Reportar Incidencia</span>
                    </button>
                  </div>

                  {ordenEnCurso?.empresaId && (() => {
                    const chatViajeId = ordenEnCurso.viajeId || ordenEnCurso.id;
                    const empresaNombre = ordenEnCurso.empresaNombre
                      || usuario?.empresa
                      || 'Transportista';
                    const unreadTrans = (store.mensajes || []).filter(m => m.viajeId === chatViajeId && m.emisorId !== usuario?.id && (!m.destinatarioRol || m.destinatarioRol === 'chofer') && m.fecha > (lastSeenMessages[chatViajeId] || '1970')).length;
                    return (
                      <button
                        onClick={() => { setChatModal({ viajeId: chatViajeId, contraparte: { id: ordenEnCurso.empresaId, nombre: empresaNombre, rol: 'transportista' } }); setLastSeenMessages(prev => { const next = {...prev, [chatViajeId]: new Date().toISOString()}; sessionStorage.setItem(_chatSeenKey, JSON.stringify(next)); return next; }); }}
                        className="w-full mt-3 px-4 py-3 bg-purple-950/40 text-purple-400 border border-purple-800 rounded-xl hover:bg-purple-900/40 flex items-center justify-center space-x-2 font-medium"
                      >
                        <MessageCircle size={20} />
                        <span>Chat con {empresaNombre}</span>
                        {unreadTrans > 0 && <span className="ml-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{unreadTrans > 9 ? '9+' : unreadTrans}</span>}
                      </button>
                    );
                  })()}
                </div>

                {/* Acciones según estado */}
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
                  {ordenEnCurso.status === 'en_camino_recogida' && (
                    <>
                      <h3 className="text-xl font-bold mb-2">🚛 En camino al punto de recogida</h3>
                      <p className="mb-4 opacity-90">Dirígete a: {ordenEnCurso.origenDir || 'Dirección no disponible'}</p>
                      <div className="grid grid-cols-2 gap-4">
                        <button 
                          onClick={() => abrirNavegacion(ordenEnCurso, 'origen')}
                          className="px-4 py-3 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 font-medium flex items-center justify-center space-x-2"
                        >
                          <Navigation size={20} />
                          <span>Abrir GPS</span>
                        </button>
                        <button 
                          onClick={confirmarLlegadaOrigen}
                          className="px-4 py-3 bg-white text-orange-600 rounded-xl hover:bg-orange-50 font-bold flex items-center justify-center space-x-2"
                        >
                          <Flag size={20} />
                          <span>Llegué al Origen</span>
                        </button>
                      </div>
                    </>
                  )}

                  {ordenEnCurso.status === 'cargando' && (
                    <>
                      <h3 className="text-xl font-bold mb-2">📦 Cargando mercancía</h3>
                      <p className="mb-4 opacity-90">Verifica la carga y cuando esté lista, inicia la ruta.</p>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          onClick={() => abrirNavegacion(ordenEnCurso, 'destino')}
                          className="px-4 py-3 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 font-medium flex items-center justify-center space-x-2"
                        >
                          <Navigation size={20} />
                          <span>Abrir GPS</span>
                        </button>
                        <button
                          onClick={confirmarCargaIniciada}
                          className="px-4 py-4 bg-white text-orange-600 rounded-xl hover:bg-orange-50 font-bold flex items-center justify-center space-x-2"
                        >
                          <Truck size={24} />
                          <span>Carga Lista - Iniciar Ruta</span>
                        </button>
                      </div>
                    </>
                  )}

                  {ordenEnCurso.status === 'en_ruta' && (
                    <>
                      <h3 className="text-xl font-bold mb-2">🛣️ En ruta hacia el destino</h3>
                      <p className="mb-4 opacity-90">Destino: {ordenEnCurso.destinoDir || 'Dirección no disponible'}</p>
                      <div className="grid grid-cols-2 gap-4">
                        <button 
                          onClick={() => abrirNavegacion(ordenEnCurso, 'destino')}
                          className="px-4 py-3 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 font-medium flex items-center justify-center space-x-2"
                        >
                          <Navigation size={20} />
                          <span>Abrir GPS</span>
                        </button>
                        <button 
                          onClick={confirmarLlegadaDestino}
                          className="px-4 py-3 bg-white text-orange-600 rounded-xl hover:bg-orange-50 font-bold flex items-center justify-center space-x-2"
                        >
                          <Flag size={20} />
                          <span>Llegué al Destino</span>
                        </button>
                      </div>
                    </>
                  )}

                  {ordenEnCurso.status === 'descargando' && (
                    <>
                      <h3 className="text-xl font-bold mb-2">📦 Descargando mercancía</h3>
                      <p className="mb-4 opacity-90">Estás en el destino. Completa la descarga y registra evidencias.</p>
                      <div className="bg-white bg-opacity-20 rounded-xl p-4 mb-4">
                        <p className="font-semibold mb-2">Checklist de entrega:</p>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            {evidenciasSubidas.length >= 2
                              ? <CheckCircle size={18} className="text-green-300" />
                              : <div className="w-4 h-4 border-2 border-white rounded-full" />}
                            <span>Fotos de evidencia ({evidenciasSubidas.length}/2 mínimo)</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {firmaCliente
                              ? <CheckCircle size={18} className="text-green-300" />
                              : <div className="w-4 h-4 border-2 border-white rounded-full" />}
                            <span>Firma del cliente</span>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <button onClick={() => setMostrarEvidencia(true)}
                          className="px-4 py-3 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 font-medium flex items-center justify-center space-x-2">
                          <Camera size={20} /><span>Cargar Fotos</span>
                        </button>
                        <button onClick={() => setMostrarFirma(true)}
                          className="px-4 py-3 bg-white bg-opacity-20 rounded-xl hover:bg-opacity-30 font-medium flex items-center justify-center space-x-2">
                          <Edit3 size={20} /><span>Obtener Firma</span>
                        </button>
                      </div>
                      <button onClick={completarOrden}
                        disabled={evidenciasSubidas.length < 2 || !firmaCliente}
                        className={`w-full px-4 py-4 rounded-xl font-bold flex items-center justify-center space-x-2 ${
                          evidenciasSubidas.length >= 2 && firmaCliente
                            ? 'bg-white text-green-600 hover:bg-green-50'
                            : 'bg-white bg-opacity-50 text-gray-400 cursor-not-allowed'
                        }`}>
                        <CheckCircle size={24} /><span>Completar Entrega</span>
                      </button>
                    </>
                  )}

                  {ordenEnCurso.status === 'incidencia' && (
                    <>
                      <h3 className="text-xl font-bold mb-2">⚠️ Viaje pausado por incidencia</h3>
                      <p className="mb-4 opacity-90">Reportaste una incidencia. Una vez resuelta, puedes continuar el viaje.</p>
                      {(() => {
                        const ultima = (ordenEnCurso.incidencias || []).slice(-1)[0];
                        if (!ultima) return null;
                        const tiposLabel = { mecanica: 'Falla mecánica', accidente: 'Accidente vial', clima: 'Condiciones climáticas', carga: 'Problema con la carga', direccion: 'Dirección incorrecta', cliente: 'Cliente no disponible', otro: 'Otro' };
                        return (
                          <div className="bg-white bg-opacity-20 rounded-xl p-3 mb-4 text-sm">
                            <p className="font-semibold">{tiposLabel[ultima.tipo] || ultima.tipo}</p>
                            <p className="opacity-90 mt-1">{ultima.descripcion}</p>
                          </div>
                        );
                      })()}
                      <button
                        onClick={() => {
                          const statusPrevio = ordenEnCurso.statusAnterior || 'en_ruta';
                          reanudarOrdenChofer(ordenEnCurso.id);
                          setOrdenEnCurso(prev => ({ ...prev, status: statusPrevio, statusAnterior: null }));
                          if (isOnline) {
                            apiActualizarStatusOrden(ordenEnCurso.id, statusPrevio).catch(() => {
                              enqueue({ action: 'updateStatus', ordenId: ordenEnCurso.id, status: statusPrevio });
                            });
                          } else {
                            enqueue({ action: 'updateStatus', ordenId: ordenEnCurso.id, status: statusPrevio });
                            mostrarNotificacion('Sin señal — se sincronizará al recuperar conexión.', 'warning');
                          }
                        }}
                        className="w-full px-4 py-4 bg-white text-orange-600 rounded-xl hover:bg-orange-50 font-bold flex items-center justify-center space-x-2"
                      >
                        <Truck size={24} />
                        <span>Reanudar Viaje</span>
                      </button>
                    </>
                  )}

                  {ordenEnCurso.status === 'llegada_destino' && (
                    <>
                      <h3 className="text-xl font-bold mb-2">🏁 ¡Llegaste al destino!</h3>
                      <p className="mb-4 opacity-90">Confirma que comenzaste a descargar la mercancía.</p>
                      <button
                        onClick={confirmarInicioDescarga}
                        className="w-full px-4 py-4 bg-white text-orange-600 rounded-xl hover:bg-orange-50 font-bold flex items-center justify-center space-x-2"
                      >
                        <Package size={24} />
                        <span>Iniciar Descarga</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-xl shadow p-12 text-center">
                <Navigation size={64} className="mx-auto text-gray-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-100 mb-2">No hay orden en curso</h3>
                <p className="text-gray-400 mb-6">Inicia una orden desde "Mis Órdenes"</p>
                <button 
                  onClick={() => setSeccion('ordenes')}
                  className="px-6 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 font-medium"
                >
                  Ver Mis Órdenes
                </button>
              </div>
            )}
          </div>
        )}

        {/* ========== HISTORIAL ========== */}
        {seccion === 'historial' && (
          <div>
            <h2 className="text-3xl font-bold text-gray-100 mb-2">Mi Historial</h2>
            <p className="text-gray-400 mb-6">Entregas completadas</p>

            {(() => {
              const { entregasMes, sinInc } = historialStats;
              return (
                <div className="bg-gray-800 rounded-xl shadow-lg mb-6 p-6">
                  <div className="grid grid-cols-4 gap-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-400 mb-1">Total Entregas</p>
                      <p className="text-3xl font-bold text-blue-400">{historial.length}</p>
                    </div>
                    <div className="text-center border-l border-gray-700">
                      <p className="text-sm text-gray-400 mb-1">Este Mes</p>
                      <p className="text-3xl font-bold text-green-400">{entregasMes.length}</p>
                    </div>
                    <div className="text-center border-l border-gray-700">
                      <p className="text-sm text-gray-400 mb-1">Sin incidencias</p>
                      <p className="text-3xl font-bold text-emerald-400">
                        {sinInc !== null ? `${sinInc}%` : '—'}
                      </p>
                    </div>
                    <div className="text-center border-l border-gray-700">
                      <p className="text-sm text-gray-400 mb-1">Calificación</p>
                      {choferInfo.viajesCompletados > 0 ? (
                        <div className="flex items-center justify-center space-x-1">
                          <p className="text-3xl font-bold text-yellow-400">{choferInfo.calificacion}</p>
                          <Star size={24} className="text-yellow-400" fill="currentColor" />
                        </div>
                      ) : (
                        <p className="text-2xl font-bold text-gray-500">—</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {historial.length === 0 ? (
              <div className="bg-gray-800 rounded-xl shadow p-12 text-center">
                <Clock size={56} className="mx-auto text-gray-600 mb-4" />
                <h3 className="text-lg font-bold text-gray-200 mb-2">Sin entregas aún</h3>
                <p className="text-gray-400 text-sm">Tus entregas completadas aparecerán aquí.</p>
              </div>
            ) : (
            <div className="bg-gray-800 rounded-xl shadow-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase">Orden</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase">Fecha</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase">Ruta</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase">Cliente</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase">Calificación</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {historial.map(orden => (
                    <tr key={orden.id} className="hover:bg-gray-700">
                      <td className="px-6 py-4">
                        <span className="font-mono font-semibold text-gray-100">{orden.id?.slice(0, 8)}…</span>
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        {orden.creadoEn ? new Date(orden.creadoEn).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-200">{orden.ruta}</td>
                      <td className="px-6 py-4 text-gray-200">{orden.cliente}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-1">
                          {[1,2,3,4,5].map(i => (
                            <Star
                              key={i}
                              size={16}
                              className={i <= orden.calificacionRecibida ? 'text-yellow-400' : 'text-gray-600'}
                              fill={i <= orden.calificacionRecibida ? 'currentColor' : 'none'}
                            />
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const viajeVinculado = orden.viajeId ? store.viajes.find(v => v.id === orden.viajeId) : null;
                          const pendiente = viajeVinculado && viajeVinculado.status === 'completada';
                          return pendiente ? (
                            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-yellow-950/40 text-yellow-300 border border-yellow-800">
                              ⏳ Pend. confirmación
                            </span>
                          ) : (
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(orden.status)}`}>
                              {getStatusText(orden.status)}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        )}

        {/* ========== RENDIMIENTO ========== */}
        {seccion === 'rendimiento' && (() => {
          const { entregasMes, calificadas, promCal, sinIncidencia, racha, porMes, mejorMes, dist } = rendimientoStats;
          return (
            <div>
              <h2 className="text-3xl font-bold text-gray-100 mb-2">Mi Desempeño</h2>
              <p className="text-gray-400 mb-6">Estadísticas de rendimiento operacional</p>

              {/* KPIs principales */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-800 rounded-xl shadow p-5 text-center">
                  <div className="w-12 h-12 bg-blue-900/40 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Package size={22} className="text-blue-400" />
                  </div>
                  <p className="text-3xl font-black text-blue-400">{choferInfo.viajesCompletados || 0}</p>
                  <p className="text-xs text-gray-400 mt-1">Entregas totales</p>
                </div>
                <div className="bg-gray-800 rounded-xl shadow p-5 text-center">
                  <div className="w-12 h-12 bg-green-900/40 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <TrendingUp size={22} className="text-green-400" />
                  </div>
                  <p className="text-3xl font-black text-green-400">{entregasMes.length}</p>
                  <p className="text-xs text-gray-400 mt-1">Este mes{entregasMes.length === 0 ? ' (sin entregas)' : ''}</p>
                </div>
                <div className="bg-gray-800 rounded-xl shadow p-5 text-center">
                  <div className="w-12 h-12 bg-emerald-900/40 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <CheckCircle size={22} className="text-emerald-400" />
                  </div>
                  <p className="text-3xl font-black text-emerald-400">{sinIncidencia !== null ? `${sinIncidencia}%` : '—'}</p>
                  <p className="text-xs text-gray-400 mt-1">Sin incidencias</p>
                </div>
                <div className="bg-gray-800 rounded-xl shadow p-5 text-center">
                  <div className="w-12 h-12 bg-orange-900/40 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Zap size={22} className="text-orange-400" />
                  </div>
                  <p className="text-3xl font-black text-orange-400">{racha}</p>
                  <p className="text-xs text-gray-400 mt-1">Racha actual</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Calificación promedio */}
                <div className="bg-gray-800 rounded-xl shadow p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Award size={20} className="text-yellow-500" />
                    <h3 className="font-bold text-gray-100">Calificación promedio</h3>
                  </div>
                  <div className="flex items-end space-x-3 mb-5">
                    <p className="text-5xl font-black text-yellow-400">{promCal ?? '—'}</p>
                    <div className="flex items-center pb-2">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} size={18}
                          className={promCal && i <= Math.round(promCal) ? 'text-yellow-400' : 'text-gray-600'}
                          fill={promCal && i <= Math.round(promCal) ? 'currentColor' : 'none'} />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mb-4">Basado en {calificadas.length} entregas calificadas</p>
                  {/* Distribución */}
                  <div className="space-y-2">
                    {dist.map(({ val, count }) => {
                      const pct = calificadas.length > 0 ? Math.round((count / calificadas.length) * 100) : 0;
                      return (
                        <div key={val} className="flex items-center space-x-2 text-xs">
                          <span className="w-3 text-gray-400 text-right">{val}</span>
                          <Star size={11} className="text-yellow-400 flex-shrink-0" fill="currentColor" />
                          <div className="flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
                            <div className="h-2 bg-yellow-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="w-8 text-gray-400 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Logros */}
                <div className="bg-gray-800 rounded-xl shadow p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Award size={20} className="text-purple-400" />
                    <h3 className="font-bold text-gray-100">Logros</h3>
                  </div>
                  <div className="space-y-3">
                    {[
                      { label: 'Primera entrega', desc: 'Completaste tu primera orden', achieved: choferInfo.viajesCompletados >= 1, icon: '🎉' },
                      { label: 'Chofer confiable', desc: '10 entregas completadas', achieved: choferInfo.viajesCompletados >= 10, icon: '🚛' },
                      { label: 'Veterano', desc: '50 entregas completadas', achieved: choferInfo.viajesCompletados >= 50, icon: '⭐' },
                      { label: 'Calificado 5★', desc: 'Promedio de 5 estrellas', achieved: promCal && parseFloat(promCal) >= 4.8, icon: '🏆' },
                      { label: 'Mejor mes', desc: mejorMes ? `${mejorMes[1]} entregas en ${mejorMes[0]}` : '5+ en un mes', achieved: mejorMes && mejorMes[1] >= 5, icon: '📅' },
                      { label: 'Racha de fuego', desc: '5 entregas consecutivas', achieved: racha >= 5, icon: '🔥' },
                    ].map(({ label, desc, achieved, icon }) => (
                      <div key={label} className={`flex items-center space-x-3 p-3 rounded-lg ${achieved ? 'bg-purple-950/40' : 'bg-gray-900 opacity-50'}`}>
                        <span className="text-2xl">{icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${achieved ? 'text-purple-300' : 'text-gray-400'}`}>{label}</p>
                          <p className="text-xs text-gray-400 truncate">{desc}</p>
                        </div>
                        {achieved && <CheckCircle size={16} className="text-purple-400 flex-shrink-0" />}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Historial de actividad mensual */}
              {Object.keys(porMes).length > 0 && (
                <div className="bg-gray-800 rounded-xl shadow p-6">
                  <h3 className="font-bold text-gray-100 mb-4">Actividad por mes</h3>
                  <div className="space-y-2">
                    {Object.entries(porMes).sort((a,b) => b[0].localeCompare(a[0])).slice(0, 6).map(([mes, cnt]) => {
                      const max = Math.max(...Object.values(porMes));
                      const pct = Math.round((cnt / max) * 100);
                      const [yr, mo] = mes.split('-');
                      const label = new Date(parseInt(yr), parseInt(mo) - 1).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
                      return (
                        <div key={mes} className="flex items-center space-x-3 text-sm">
                          <span className="w-32 text-gray-400 capitalize text-xs truncate">{label}</span>
                          <div className="flex-1 bg-gray-700 rounded-full h-3 overflow-hidden">
                            <div className="h-3 bg-orange-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="w-6 text-gray-200 font-semibold text-xs text-right">{cnt}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {historial.length === 0 && (
                <div className="bg-gray-800 rounded-xl shadow p-12 text-center mt-6">
                  <TrendingUp size={56} className="mx-auto text-gray-600 mb-4" />
                  <h3 className="text-lg font-bold text-gray-200 mb-2">Sin datos aún</h3>
                  <p className="text-gray-400 text-sm">Tu desempeño se mostrará aquí después de tu primera entrega.</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* ========== PERFIL ========== */}
        {seccion === 'perfil' && (
          <div>
            <h2 className="text-3xl font-bold text-gray-100 mb-2">Mi Perfil</h2>
            <p className="text-gray-400 mb-6">Información de tu cuenta</p>

            {/* Avatar editable */}
            <div className="bg-gray-800 rounded-xl shadow-lg p-6 mb-6 flex items-center space-x-5">
              <label className="relative cursor-pointer group flex-shrink-0" title="Cambiar foto de perfil">
                <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center text-white overflow-hidden">
                  {(() => {
                    const foto = store.usuarioPerfil?.avatar || usuario?.avatar;
                    return foto
                      ? <img src={foto} alt="avatar" className="w-full h-full object-cover" />
                      : <User size={36} />;
                  })()}
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-full flex items-center justify-center transition-all">
                  <span className="text-white text-xs font-semibold opacity-0 group-hover:opacity-100">Cambiar</span>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
              </label>
              <div>
                <p className="text-xl font-bold text-gray-100">{choferInfo.nombre}</p>
                <p className="text-gray-400 text-sm">{usuario?.email}</p>
                <p className="text-xs text-gray-400 mt-1">Haz clic en la foto para cambiarla</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="col-span-2 bg-gray-800 rounded-xl shadow-lg p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold flex items-center text-gray-100">
                    <User size={24} className="mr-2 text-orange-500" />
                    Información Personal
                  </h3>
                  <button
                    onClick={() => { setFormPerfil({}); setEditandoPerfil(true); }}
                    className="flex items-center space-x-1 px-3 py-2 text-sm bg-orange-900/30 text-orange-400 rounded-lg hover:bg-orange-900/50 font-medium"
                  >
                    <Edit3 size={15} /><span>Cambiar contraseña</span>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Nombre Completo</label>
                    <p className="px-4 py-3 bg-gray-900 rounded-lg font-semibold text-gray-100">{choferInfo.nombre}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Teléfono</label>
                    <p className="px-4 py-3 bg-gray-900 rounded-lg font-semibold text-gray-100">{choferInfo.telefono}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Licencia</label>
                    <p className="px-4 py-3 bg-gray-900 rounded-lg font-mono font-semibold text-gray-100">{choferInfo.licencia}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Empresa</label>
                    <p className="px-4 py-3 bg-gray-900 rounded-lg font-semibold text-gray-100">{choferInfo.empresa}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-400 mb-2">Unidad Asignada</label>
                    {choferInfo.tipoUnidadAsignada ? (
                      <div className="rounded-xl overflow-hidden border border-gray-700">
                        <TipoUnidadBadge
                          tipo={choferInfo.tipoUnidadAsignada}
                          marcaModelo={[choferInfo.marcaUnidadAsignada, choferInfo.modeloUnidadAsignada, choferInfo.anioUnidadAsignada].filter(Boolean).join(' ') || null}
                          foto={choferInfo.fotoUnidadAsignada}
                          gpsInstalado={choferInfo.gpsUnidadAsignada}
                          largoM={choferInfo.largoMUnidadAsignada}
                          anchoM={choferInfo.anchoMUnidadAsignada}
                          altoM={choferInfo.altoMUnidadAsignada}
                          seguroCarga={choferInfo.seguroCargaUnidadAsignada}
                          tempMinima={choferInfo.tempMinimaUnidadAsignada}
                        />
                        {choferInfo.placas && (
                          <div className="px-4 py-2 bg-gray-900 flex items-center gap-2 border-t border-gray-700">
                            <span className="text-xs text-gray-400">Placas:</span>
                            <span className="font-mono font-bold text-gray-100 tracking-wider">{choferInfo.placas}</span>
                            {choferInfo.capacidadUnidadAsignada && (
                              <span className="ml-auto text-xs text-gray-400">{choferInfo.capacidadUnidadAsignada}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="px-4 py-3 bg-gray-900 rounded-lg flex items-center gap-2">
                        <Truck size={18} className="text-gray-500" />
                        <span className="text-gray-400 text-sm">{choferInfo.unidadAsignada || 'Sin unidad asignada'}</span>
                        {choferInfo.placas && <span className="ml-auto font-mono text-gray-300 text-sm">{choferInfo.placas}</span>}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold mb-6 flex items-center text-gray-100">
                  <Shield size={24} className="mr-2 text-green-400" />
                  Estadísticas
                </h3>

                <div className="space-y-4">
                  <div className="p-4 bg-blue-950/40 rounded-xl">
                    <p className="text-sm text-blue-400">Entregas Completadas</p>
                    <p className="text-3xl font-bold text-blue-300">{choferInfo.viajesCompletados}</p>
                  </div>

                  <div className="p-4 bg-green-950/40 rounded-xl">
                    <p className="text-sm text-green-400">Calificación</p>
                    {choferInfo.viajesCompletados > 0 ? (
                      <div className="flex items-center space-x-2">
                        <p className="text-3xl font-bold text-green-300">{choferInfo.calificacion}</p>
                        <div className="flex">
                          {[1,2,3,4,5].map(i => (
                            <Star key={i} size={20}
                              className={i <= Math.round(choferInfo.calificacion) ? 'text-yellow-400' : 'text-gray-600'}
                              fill={i <= Math.round(choferInfo.calificacion) ? 'currentColor' : 'none'}
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-2xl font-bold text-gray-500">Sin calificación aún</p>
                    )}
                  </div>

                  <div className="p-4 bg-purple-950/40 rounded-xl">
                    <p className="text-sm text-purple-400">Km Recorridos</p>
                    <p className="text-3xl font-bold text-purple-300">{choferInfo.kmRecorridos.toLocaleString()}</p>
                  </div>

                  <div className="p-4 bg-orange-900/30 rounded-xl">
                    <p className="text-sm text-orange-400">Antigüedad</p>
                    <p className="text-2xl font-bold text-orange-300">{choferInfo.antiguedad}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* MODALES */}
      {/* Input oculto para captura real de cámara */}
      <input ref={fotoInputRef} type="file" accept="image/*" capture="environment"
        className="hidden" onChange={onFotoCapturada} />
      {/* Input oculto para selección desde galería */}
      <input ref={fotoGaleriaRef} type="file" accept="image/*"
        className="hidden" onChange={onFotoCapturada} />
      {mostrarIncidencia && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-100">Reportar Incidencia</h3>
                <button onClick={() => setMostrarIncidencia(false)} className="text-gray-400 hover:text-gray-200">
                  <X size={24} />
                </button>
              </div>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!formIncidencia.tipo || !formIncidencia.descripcion.trim()) return;
                setIncidenciaEnvio(true);
                try {
                  await apiReportarIncidencia(ordenActiva?.id, formIncidencia);
                  reportarIncidencia(ordenActiva?.id, formIncidencia);
                  setMostrarIncidencia(false);
                  setFormIncidencia({ tipo: '', descripcion: '', foto: null });
                  mostrarNotificacion('Incidencia reportada. Tu empresa ha sido notificada.', 'warning');
                } catch {
                  mostrarNotificacion('Error al reportar la incidencia. Intenta de nuevo.', 'error');
                } finally {
                  setIncidenciaEnvio(false);
                }
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-2">Tipo de Incidencia</label>
                    <select
                      value={formIncidencia.tipo}
                      onChange={e => setFormIncidencia(f => ({ ...f, tipo: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-600 bg-gray-900 text-gray-100 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required
                    >
                      <option value="">Seleccionar...</option>
                      <option value="mecanica">Falla mecánica</option>
                      <option value="accidente">Accidente vial</option>
                      <option value="clima">Condiciones climáticas</option>
                      <option value="carga">Problema con la carga</option>
                      <option value="direccion">Dirección incorrecta</option>
                      <option value="cliente">Cliente no disponible</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-2">Descripción</label>
                    <textarea
                      rows="4"
                      value={formIncidencia.descripcion}
                      onChange={e => setFormIncidencia(f => ({ ...f, descripcion: e.target.value }))}
                      placeholder="Describe la incidencia con detalle..."
                      className="w-full px-4 py-3 border border-gray-600 bg-gray-900 text-gray-100 placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-2">
                      <Camera size={16} className="inline mr-1" /> Adjuntar Foto (Opcional)
                    </label>
                    {formIncidencia.foto ? (
                      <div className="relative">
                        <img src={formIncidencia.foto} alt="Evidencia" className="w-full h-32 object-cover rounded-lg" />
                        <button type="button" onClick={() => setFormIncidencia(f => ({ ...f, foto: null }))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1">
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-600 rounded-lg p-4">
                        <div className="flex space-x-2">
                          <label className="flex-1 flex flex-col items-center py-3 bg-gray-700 hover:bg-gray-600 rounded-lg cursor-pointer">
                            <Camera size={24} className="text-gray-300 mb-1" />
                            <p className="text-xs text-gray-300 font-medium">Cámara</p>
                            <input type="file" accept="image/*" capture="environment" className="hidden"
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const reader = new FileReader();
                                reader.onload = ev => setFormIncidencia(f => ({ ...f, foto: ev.target.result }));
                                reader.readAsDataURL(file);
                              }}
                            />
                          </label>
                          <label className="flex-1 flex flex-col items-center py-3 bg-gray-700 hover:bg-gray-600 rounded-lg cursor-pointer">
                            <ImagePlus size={24} className="text-gray-300 mb-1" />
                            <p className="text-xs text-gray-300 font-medium">Galería</p>
                            <input type="file" accept="image/*" className="hidden"
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const reader = new FileReader();
                                reader.onload = ev => setFormIncidencia(f => ({ ...f, foto: ev.target.result }));
                                reader.readAsDataURL(file);
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex space-x-4 mt-6">
                  <button
                    type="button"
                    onClick={() => { setMostrarIncidencia(false); setFormIncidencia({ tipo: '', descripcion: '', foto: null }); }}
                    className="flex-1 px-4 py-3 border border-gray-600 text-gray-200 rounded-lg hover:bg-gray-700 font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={incidenciaEnvio}
                    className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium flex items-center justify-center space-x-2 disabled:opacity-60"
                  >
                    <Send size={18} />
                    <span>Enviar Reporte</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {mostrarEvidencia && ModalEvidencia()}
      {mostrarFirma && ModalFirma()}
      {mostrarResumen && ModalResumen()}
      {mostrarDetalleOrden && ModalDetalleOrden()}

      {/* Modal: Cambiar contraseña */}
      {editandoPerfil && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-sm w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-lg font-bold text-gray-100">Cambiar contraseña</h3>
                <button onClick={() => setEditandoPerfil(false)} className="text-gray-400 hover:text-gray-200"><X size={22} /></button>
              </div>
              <form onSubmit={async e => {
                e.preventDefault();
                const { passwordActual, passwordNuevo, passwordConfirm } = formPerfil;
                if (!passwordActual || !passwordNuevo) return;
                if (passwordNuevo.length < 8) { mostrarNotificacion('La contraseña debe tener al menos 8 caracteres', 'error'); return; }
                if (passwordNuevo !== passwordConfirm) { mostrarNotificacion('Las contraseñas no coinciden', 'error'); return; }
                try {
                  await apiCambiarPassword({ passwordActual, passwordNuevo });
                  mostrarNotificacion('Contraseña actualizada correctamente');
                  setEditandoPerfil(false);
                  setFormPerfil({});
                } catch (err) {
                  mostrarNotificacion(err.message || 'Contraseña actual incorrecta', 'error');
                }
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-1">Contraseña actual</label>
                    <input type="password" value={formPerfil.passwordActual || ''}
                      onChange={e => setFormPerfil(f => ({ ...f, passwordActual: e.target.value }))}
                      placeholder="Tu contraseña actual"
                      className="w-full px-4 py-3 border border-gray-600 bg-gray-900 text-gray-100 placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-1">Nueva contraseña</label>
                    <input type="password" value={formPerfil.passwordNuevo || ''}
                      onChange={e => setFormPerfil(f => ({ ...f, passwordNuevo: e.target.value }))}
                      placeholder="Mínimo 8 caracteres"
                      minLength={8}
                      className="w-full px-4 py-3 border border-gray-600 bg-gray-900 text-gray-100 placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-1">Confirmar contraseña</label>
                    <input type="password" value={formPerfil.passwordConfirm || ''}
                      onChange={e => setFormPerfil(f => ({ ...f, passwordConfirm: e.target.value }))}
                      placeholder="Repite la contraseña"
                      className="w-full px-4 py-3 border border-gray-600 bg-gray-900 text-gray-100 placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required />
                  </div>
                  <p className="text-xs text-gray-400">Tu teléfono y demás datos son administrados por tu empresa.</p>
                </div>
                <div className="flex space-x-3 mt-5">
                  <button type="button" onClick={() => { setEditandoPerfil(false); setFormPerfil({}); }}
                    className="flex-1 px-4 py-2.5 border border-gray-600 text-gray-200 rounded-lg hover:bg-gray-700 font-medium text-sm">
                    Cancelar
                  </button>
                  <button type="submit"
                    className="flex-1 px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium text-sm">
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Chat */}
      {chatModal && (
        <ChatModal
          viajeId={chatModal.viajeId}
          usuarioId={usuario?.id}
          usuarioNombre={usuario?.nombre || choferInfo.nombre}
          usuarioRol="chofer"
          contraparte={chatModal.contraparte}
          onClose={() => { setLastSeenMessages(prev => { const next = {...prev, [chatModal.viajeId]: new Date().toISOString()}; sessionStorage.setItem(_chatSeenKey, JSON.stringify(next)); return next; }); setChatModal(null); }}
        />
      )}
      </div> {/* fin flex-1 contenido principal */}

      {/* ── Barra de navegación inferior — solo móvil ── */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[100] md:hidden bg-gray-900 border-t border-gray-700"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex items-center h-14">
          {/* Mis Órdenes */}
          <button
            onClick={() => setSeccion('ordenes')}
            className={`flex-1 flex flex-col items-center justify-center h-full gap-0.5 text-[10px] font-medium transition-colors relative ${seccion === 'ordenes' ? 'text-orange-400' : 'text-gray-500'}`}
          >
            <Package size={20} />
            <span>Órdenes</span>
            {(ordenesAsignadas.length + ordenesEnProgreso.length) > 0 && (
              <span className="absolute top-2 right-4 w-4 h-4 bg-orange-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                {ordenesAsignadas.length + ordenesEnProgreso.length}
              </span>
            )}
          </button>

          {/* Orden Actual */}
          <button
            onClick={() => setSeccion('en_curso')}
            className={`flex-1 flex flex-col items-center justify-center h-full gap-0.5 text-[10px] font-medium transition-colors relative ${seccion === 'en_curso' ? 'text-orange-400' : 'text-gray-500'}`}
          >
            <Navigation size={20} />
            <span>En Curso</span>
            {ordenesEnProgreso.length > 0 && (
              <span className="absolute top-2 right-4 w-4 h-4 bg-green-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                {ordenesEnProgreso.length}
              </span>
            )}
          </button>

          {/* Historial */}
          <button
            onClick={() => setSeccion('historial')}
            className={`flex-1 flex flex-col items-center justify-center h-full gap-0.5 text-[10px] font-medium transition-colors ${seccion === 'historial' ? 'text-orange-400' : 'text-gray-500'}`}
          >
            <Clock size={20} />
            <span>Historial</span>
          </button>

          {/* Mi Desempeño */}
          <button
            onClick={() => setSeccion('rendimiento')}
            className={`flex-1 flex flex-col items-center justify-center h-full gap-0.5 text-[10px] font-medium transition-colors ${seccion === 'rendimiento' ? 'text-orange-400' : 'text-gray-500'}`}
          >
            <TrendingUp size={20} />
            <span>Desempeño</span>
          </button>

          {/* Mi Perfil */}
          <button
            onClick={() => setSeccion('perfil')}
            className={`flex-1 flex flex-col items-center justify-center h-full gap-0.5 text-[10px] font-medium transition-colors ${seccion === 'perfil' ? 'text-orange-400' : 'text-gray-500'}`}
          >
            <User size={20} />
            <span>Perfil</span>
          </button>
        </div>
      </div>

      <FeedbackButton />
    </div>
  );
}

export default AppChofer;